import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';
import { ActivatedRoute, Router } from '@angular/router';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import * as pdfjsLib from 'pdfjs-dist';
import { PDFDocument } from 'pdf-lib';
import { firstValueFrom, Subscription } from 'rxjs';
import {
  SequencingPollDocument,
  SequencingPollExtractedField,
  SequencingPollSequenceResponse,
  TuneHilStatus,
  TuneSaveAnnotationsRequest,
  TuneSourcePdfUrlResponse,
} from '../../models/truth-management.model';
import { AuthService } from '../../services/auth.service';
import { ModalService } from '../../services/modal.service';
import { NotificationService } from '../../services/notification.service';
import { TruthManagement } from '../../services/truth-management';
import { PageHeaderComponent } from '../page-header/page-header.component';
import { PdfPageRenderedEvent } from './pdf-page-canvas.directive';
import { PdfLazyViewerComponent } from '../shared/pdf-lazy-viewer/pdf-lazy-viewer.component';
import { PdfPagesScrollDirective } from './pdf-pages-virtual-scroll';
import { sourcePdfCache } from './source-pdf-cache';

type TimeFilter = 'today' | 'last7' | 'last30' | 'custom';
const SHARED_DATE_FILTER_KEY = 'docsila.sharedDateFilter.operationsScope';

// Configure PDF.js worker once at module load. pdfjs-dist v4 is ESM-only and
// its build/pdf.worker.min.mjs is available on the same CDN under the pinned
// version. See package.json for the currently-installed version.
if (typeof window !== 'undefined' && !(pdfjsLib as any).GlobalWorkerOptions.workerSrc) {
  (pdfjsLib as any).GlobalWorkerOptions.workerSrc =
    'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.8.69/pdf.worker.min.mjs';
}

/**
 * Custom PDF data-range transport for pdf.js. Fetches only the byte ranges
 * pdf.js asks for — instead of handing pdf.js the URL and letting it open one
 * long-lived request that keeps transferring the whole file even after pdf.js
 * stops reading.
 *
 * pdf.js has no per-range error channel: a range that never arrives leaves the
 * page waiting forever. So failures are retried with backoff (warmPdfCache is
 * filling the HTTP cache in the background, so a later retry is served
 * locally), and a 200 — a server ignoring `Range` — is sliced to the requested
 * window instead of being fed whole at `begin`, which would corrupt the doc.
 */
class BlobRangeTransport extends (pdfjsLib as any).PDFDataRangeTransport {
  private static readonly RETRY_DELAYS_MS = [250, 500, 1000];
  private localBlob: Blob | null;

  constructor(length: number, private readonly rangeUrl: string, localBlob: Blob | null = null) {
    super(length, null, false);
    this.localBlob = localBlob;
  }

  /** Once the whole file is stored locally, serve every further range from it. */
  useLocalBlob(blob: Blob): void {
    this.localBlob = blob;
  }

  requestDataRange(begin: number, end: number): void {
    if (this.localBlob) {
      void this.readLocalRange(begin, end);
      return;
    }
    void this.fetchRange(begin, end, 0);
  }

  /** blob.slice() reads only the requested window from disk — memory stays lazy. */
  private async readLocalRange(begin: number, end: number): Promise<void> {
    try {
      const buf = await this.localBlob!.slice(begin, end).arrayBuffer();
      (this as any).onDataRange(begin, new Uint8Array(buf));
    } catch (err) {
      this.localBlob = null;
      void this.fetchRange(begin, end, 0);
    }
  }

  private async fetchRange(begin: number, end: number, attempt: number): Promise<void> {
    try {
      const response = await fetch(this.rangeUrl, { headers: { Range: `bytes=${begin}-${end - 1}` } });
      if (response.status !== 206 && response.status !== 200) {
        throw new Error(`HTTP ${response.status}`);
      }
      let bytes = new Uint8Array(await response.arrayBuffer());
      if (response.status === 200 && bytes.length > end - begin) {
        bytes = bytes.slice(begin, end);
      }
      (this as any).onDataRange(begin, bytes);
    } catch (err) {
      const delay = BlobRangeTransport.RETRY_DELAYS_MS[attempt];
      if (delay !== undefined) {
        setTimeout(() => void this.fetchRange(begin, end, attempt + 1), delay);
        return;
      }
    }
  }

  abort(): void { /* no-op */ }
}

interface SourceIndexingRow {
  loanNumber: string;
  documentCount: number;
  lastModifiedOn: string;
  lastModifiedBy: string;
  status: 'Active' | 'Pending' | 'On Hold' | 'Completed';
  actions: string;
  requestId?: string;
  clientId?: string;
  fileName?: string;
}

interface SourceLoanContext {
  loanNumber: string;
  row: SourceIndexingRow;
  requestId?: string;
}

interface SourceFileItem {
  id: string;
  fileName: string;
  subFileName: string;
  version: string;
  matchTypeCode: string;
  matchTypeLabel: string;
  matchTypeHowDetermined: string;
  matchTypeBadgeClass: string;
  templateId: string;
  startPageNumber: number | null;
  endPageNumber: number | null;
  totalDocumentPages: number | null;
  pageRangeSummary: string;
}

interface SourceMatchTypeMeta {
  label: string;
  howDetermined: string;
  badgeClass: string;
}

interface AnnotationPanelState {
  documentName: string;
  applyToAll: boolean;
  applyUp: boolean;
  applyDown: boolean;
  applyToAllBackup: Record<number, string> | null;
  applyUpBackup: Record<number, string> | null;
  applyDownBackup: Record<number, string> | null;
  comments: string;
  isDocumentNameEditable: boolean;
}

interface AnnotationSaveSnapshot {
  comments: string;
  documentName: string;
}

type SourceReviewStatus = 'under-review' | 'completed' | 'failed';

type SourceAttributeFieldType = 'text' | 'date' | 'select';

interface SourceAttributeOption {
  value: string;
  displayText: string;
  fullDescription: string;
}

interface SourceAttributeItem {
  id: number;
  attributeLabel: string;
  description: string;
  fieldType: SourceAttributeFieldType;
  isArray: boolean;
  initialValues: string[];
  allowedValues?: SourceAttributeOption[];
}

interface SharedDateFilterState {
  selectedTimeFilter: TimeFilter;
  startDate: string;
  endDate: string;
  startDateDisplay: string;
  endDateDisplay: string;
  updatedAt: number;
}

interface SourceFileAttributeState {
  attributes: SourceAttributeItem[];
  values: Record<number, string[]>;
  originalValues: Record<number, string[]>;
  notApplicable: Set<number>;
  originalNotApplicable: Set<number>;
}

interface SourceFieldSourceData {
  pageNumber: number;
  boundingBoxes: number[][];
  pageWidth: number;
  boundingBoxIndices?: number[][];
}

interface SourceOverlayBox {
  left: number;
  top: number;
  width: number;
  height: number;
  indices: number[];
}


interface AnnotationDocumentTypesCacheState {
  module: string;
  documentTypes: string[];
}

const SOURCE_LOAN_CONTEXT_STORAGE_KEY = 'source:selected-loan-context';
const SOURCE_ANNOTATION_DOCUMENT_TYPES_STORAGE_KEY = 'source:annotation-document-types';
const SOURCE_PREVIEW_PDF_CACHE_PREFIX = 'source:preview-pdf:';

const SOURCE_MATCH_TYPE_META_BY_CODE: Record<string, SourceMatchTypeMeta> = {
  TPL: {
    label: 'Template',
    howDetermined: 'Page text matched a trained template via similarity index',
    badgeClass: 'match-template',
  },
  SPA: {
    label: 'Spatial',
    howDetermined: 'Field regions matched expected anchors by page geometry and positional layout',
    badgeClass: 'match-spatial',
  },
  KPM: {
    label: 'Key Phrase Match',
    howDetermined: 'Required key phrases were detected and met configured match criteria',
    badgeClass: 'match-keyphrase',
  },
  KEY: {
    label: 'Keyword',
    howDetermined: 'Configured keyword rules matched the detected page content',
    badgeClass: 'match-keyword',
  },
  EXA: {
    label: 'Exact',
    howDetermined: 'Exact text pattern rules matched the detected content without tolerance',
    badgeClass: 'match-exact',
  },
  FUZ: {
    label: 'Fuzzy',
    howDetermined: 'Fuzzy similarity thresholds matched close text variants above threshold',
    badgeClass: 'match-fuzzy',
  },
  PRF: {
    label: 'Prefix',
    howDetermined: 'Prefix rule conditions matched the beginning of detected text values',
    badgeClass: 'match-prefix',
  },
  SUF: {
    label: 'Suffix',
    howDetermined: 'Suffix rule conditions matched the ending of detected text values',
    badgeClass: 'match-suffix',
  },
  LLM: {
    label: 'LLM',
    howDetermined: 'Large language model classification selected the best matching document type',
    badgeClass: 'match-llm',
  },
  LMS: {
    label: 'LLM Semantic',
    howDetermined: 'Semantic similarity scoring from the language model determined the closest match',
    badgeClass: 'match-llm-semantic',
  },
  LMN: {
    label: 'LLM Named',
    howDetermined: 'Named entities extracted by the language model matched expected document signals',
    badgeClass: 'match-llm-named',
  },
  LMP: {
    label: 'LLM Prompt',
    howDetermined: 'Prompt-evaluated language model output mapped to the best document type',
    badgeClass: 'match-llm-prompt',
  },
  CON: {
    label: 'Context',
    howDetermined: 'Contextual inference across nearby fields and document sections determined the match',
    badgeClass: 'match-context',
  },
  EXT: {
    label: 'Extraction',
    howDetermined: 'Extraction-level confidence signals and heuristics selected this document type',
    badgeClass: 'match-extraction',
  },
  ORP: {
    label: 'Override',
    howDetermined: 'Override policy or configured precedence rules explicitly set this match',
    badgeClass: 'match-override',
  },
};

@Component({
  selector: 'app-source',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCalendar,
    MatNativeDateModule,
    PdfLazyViewerComponent,
    PageHeaderComponent,
  ],
  templateUrl: './source.component.html',
  styleUrls: ['./source.component.scss'],
})
export class SourceComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('contentGrid') contentGrid?: ElementRef<HTMLDivElement>;
  // The lazy-loading viewport lives inside app-pdf-lazy-viewer now. These
  // getters keep every existing call site (`previewScroller.nativeElement`
  // clientWidth / scrollTop / querySelector of .pdf-page-wrapper[data-page],
  // pdfViewport scroll offsets, pdfScroll.strategy) working unchanged.
  @ViewChild(PdfLazyViewerComponent) private lazyViewer?: PdfLazyViewerComponent;
  get previewScroller(): ElementRef<HTMLElement> | undefined {
    const el = this.lazyViewer?.scrollerElement;
    return el ? new ElementRef(el) : undefined;
  }
  get pdfViewport(): CdkVirtualScrollViewport | undefined {
    return this.lazyViewer?.viewport;
  }
  get pdfScroll(): PdfPagesScrollDirective | undefined {
    return this.lazyViewer?.scrollDirective;
  }

  isAuthenticated = false;
  isRoleResolved = false;

  selectedLoanContext: SourceLoanContext | null = null;
  sourceRequestId = '';
  sourceFiles: SourceFileItem[] = [];
  selectedFileId = '';
  fileSearchTerm = '';
  fileFilterKind: 'all' | 'misc' = 'all';
  isFileColumnCollapsed = false;
  isSourceDataLoading = false;
  sourceDataMessage = '';

  selectedTimeFilter: TimeFilter = 'today';
  startDate = '';
  endDate = '';
  startDateDisplay = '';
  endDateDisplay = '';
  endDateInvalid = false;
  dateRangeValidationMessage = '';
  startHour12 = '12';
  startMinute = '00';
  startPeriod: 'AM' | 'PM' = 'AM';
  endHour12 = '12';
  endMinute = '00';
  endPeriod: 'AM' | 'PM' = 'AM';
  readonly hourOptions = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
  readonly minuteOptions = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
  activeDateTimePopup: 'start' | 'end' | null = null;
  popupDate: Date | null = null;
  popupHour12 = '12';
  popupMinute = '00';
  popupPeriod: 'AM' | 'PM' = 'AM';
  private popupDirty = false;

  activeRightTab: 'annotation' | 'reclassification' = 'annotation';
  sourceReviewStatus: SourceReviewStatus = 'under-review';
  private sourceReviewStatusBaseline: SourceReviewStatus = 'under-review';
  isAnnotationSaveInProgress = false;
  showAttributesSavedDot = false;
  annotationPages = [1, 2, 3, 4];
  expandedAnnotationPages = new Set<number>();
  annotationPanelStateByPage: Record<number, AnnotationPanelState> = {};
  annotationDocumentTypeOptions: string[] = [];
  activeAnnotationDocumentTypeDropdownPage: number | null = null;
  annotationDocumentTypeSearchByPage: Record<number, string> = {};
  annotationDocumentTypeDropdownStyle: Record<string, string> | null = null;
  activeAnnotationApplyMenuPage: number | null = null;
  private annotationSaveBaselineByPage: Record<number, AnnotationSaveSnapshot> = {};
  private annotationSaveBaselineFileId: string | null = null;
  editedDocNameByPage: Record<number, string> = {};
  existingCommentsByPage: Record<number, string> = {};

  attributeSearchTerm = '';
  sourceAttributes: SourceAttributeItem[] = [
    {
      id: 101,
      attributeLabel: 'Borrower Name',
      description: 'Primary borrower full name as shown in the document.',
      fieldType: 'text',
      isArray: false,
      initialValues: ['Jordan Miles'],
    },
    {
      id: 102,
      attributeLabel: 'Date of Birth',
      description: 'Borrower date of birth in MM/DD/YYYY format.',
      fieldType: 'date',
      isArray: false,
      initialValues: ['03/18/1986'],
    },
    {
      id: 103,
      attributeLabel: 'Occupancy Type',
      description: 'Occupancy declaration for the collateral property.',
      fieldType: 'select',
      isArray: false,
      initialValues: ['primary_residence'],
      allowedValues: [
        { value: 'primary_residence', displayText: 'Primary Residence', fullDescription: 'Primary Residence - Borrower occupied' },
        { value: 'second_home', displayText: 'Second Home', fullDescription: 'Second Home - Seasonal or vacation use' },
        { value: 'investment', displayText: 'Investment', fullDescription: 'Investment - Non-owner occupied property' },
      ],
    },
    {
      id: 104,
      attributeLabel: 'Monthly Income Sources',
      description: 'List of income sources declared in the file.',
      fieldType: 'text',
      isArray: true,
      initialValues: ['Base Salary', 'Rental Income'],
    },
    {
      id: 105,
      attributeLabel: 'Document Received Dates',
      description: 'One or more received dates for supporting documents.',
      fieldType: 'date',
      isArray: true,
      initialValues: ['05/10/2026'],
    },
    {
      id: 106,
      attributeLabel: 'Property Use Flags',
      description: 'Enumerated usage classifications captured from source docs.',
      fieldType: 'select',
      isArray: true,
      initialValues: ['purchase'],
      allowedValues: [
        { value: 'purchase', displayText: 'Purchase', fullDescription: 'Purchase transaction' },
        { value: 'refinance_rate_term', displayText: 'Refinance - Rate/Term', fullDescription: 'Refinance for rate/term change' },
        { value: 'refinance_cash_out', displayText: 'Refinance - Cash Out', fullDescription: 'Refinance with cash out' },
      ],
    },
  ];
  sourceAttributeValues: Record<number, string[]> = {};
  sourceOriginalAttributeValues: Record<number, string[]> = {};
  sourceNotApplicableAttributes = new Set<number>();
  sourceOriginalNotApplicableAttributes = new Set<number>();
  editingAttributeId: number | null = null;
  editingArrayIndex: number | null = null;
  editingFieldType: SourceAttributeFieldType = 'text';
  editingTruthValueText = '';

  currentPage = 1;
  totalPages = 0;
  pageInput: number | string = 1;
  pdfSrc = '';
  isPreviewPdfLoading = false;
  previewPdfLoadError = '';

  // Custom PDF.js pipeline state. Scrolling is owned by Angular CDK virtual scroll
  // (pdf-pages-virtual-scroll.ts); per-page rendering by PdfPageCanvasDirective.
  downloadingFileIds = new Set<string>();
  pdfLoadProgress = 0;                                       // 0-100, warm-cache progress bar
  get sourcePdfDocReady(): boolean { return !!this.sourcePdfDoc; }
  sourcePdfDoc: any = null;                                  // PDFDocumentProxy (template-bound)
  private sourcePageSizesFromServer: { width: number; height: number }[] = [];
  private warmCacheAbortController: AbortController | null = null;
  // Layout model consumed by the template and the scroll strategy — one entry
  // per page, in px, exact canvas pixel size (unfloored) at the current zoom.
  // The strategy turns pageHeightsPx + pageGapPx into the authoritative offset
  // table; nothing the browser does while painting a page can move another one.
  pageNumbers: number[] = [];
  pageWidthsPx: number[] = [];
  pageHeightsPx: number[] = [];
  pageScales: number[] = [];
  // The gap between pages is specified in rem (matches `.pdf-page-wrapper`'s
  // `margin: 1rem auto 0`); computePageLayout derives the px value the CDK
  // strategy needs from the live root font size so the two never disagree.
  readonly pageGapRem = 1;
  pageGapPx = 16;
  readonly trackByPageNumber = (_index: number, page: number): number => page;
  pxPerRem = 16;
  // Set by computePageLayout, cleared by scrollToPage. The strategy emits index 0
  // as soon as a layout lands — before we've jumped to the intended page — and
  // acting on it would momentarily activate the first document and flash the
  // attribute panel. Every layout is immediately followed by a scrollToPage.
  private ignoreScrollIndexUntilProgrammaticScroll = false;
  private sourceBoundingBoxSuppressionTimeout: ReturnType<typeof setTimeout> | null = null;
  private previewResizeTimeout: ReturnType<typeof setTimeout> | null = null;
  // Monotonic counter for "activate document" calls. Bumped every time
  // activateDocumentById() runs so any async follow-up (e.g. the RAF-scroll of
  // the file tree) can bail out when a faster activation has superseded it.
  // Mirrors the reference implementation's `dataRequestSeq` pattern and is
  // the seam where a per-doc `/tune/{req}/documents/{id}` API can slot in
  // later without touching the sidebar click / scroll callers.
  private docActivationSeq = 0;

  zoom = 1;
  zoomPercentage = 100;
  readonly minZoom = 0.5;
  readonly maxZoom = 3;
  readonly isSourceValueReadOnly = true;

  pageRotations = new Map<number, number>();

  private sourceBoundingBoxRecenterTimeout: ReturnType<typeof setTimeout> | null = null;
  private sourceBoundingBoxReRenderTimer: ReturnType<typeof setTimeout> | null = null;
  private previewResizeObserver: ResizeObserver | null = null;
  private previewZoomRestoreFrame: number | null = null;
  private previewZoomRestoreTimeouts: ReturnType<typeof setTimeout>[] = [];
  private sequencingPollSub?: Subscription;
  private sequencingDocumentTypesSub?: Subscription;
  private sequencingExecutionDataSub?: Subscription;
  private currentClientSub?: Subscription;
  private lastKnownClientId: string | null = null;
  private sourceFileAttributeStateByFileId = new Map<string, SourceFileAttributeState>();
  private sourceFieldSourceDataByFileId = new Map<string, Map<number, SourceFieldSourceData>>();
  private sourceFieldSourceDataMap = new Map<number, SourceFieldSourceData>();
  private highlightedSourceAttributeId: number | null = null;
  private activeSourceBoundingBoxes: SourceOverlayBox[] = [];
  private annotationDefaultDocumentNameByFileId = new Map<string, string>();
  private activeAnnotationStateFileId: string | null = null;
  // Per-document snapshot of the annotation rows (name / comments / apply
  // flags). annotationPanelStateByPage is keyed by LOCAL page and shared across
  // documents, so without this a switch would overwrite unsaved edits.
  private annotationPanelStateByFileId = new Map<string, Record<number, AnnotationPanelState>>();
  private get annotationDocumentTypesModule(): string { return this.authService.getCurrentClientModule(); }
  private previewPdfBlobUrl = '';
  private previewPdfRequestEpoch = 0;

  private filesColumnWidthPx = 320;
  private tuningColumnWidthPx = 420;
  private readonly splitterWidthPx = 10;
  private readonly collapsedFilesColumnWidthPx = 40;
  private readonly minFilesColumnWidthPx = 180;
  private readonly minViewerColumnWidthPx = 360;
  private readonly minTuningColumnWidthPx = 300;
  private readonly desktopBreakpointPx = 992;
  private readonly defaultFilesRatio = 1 / 4.2;
  private readonly defaultTuningRatio = 1 / 4.2;
  private hasUserCustomizedColumnWidths = false;
  private collapsedPreviewWidthPx: number | null = null;

  activeColumnSplitter: 'files-viewer' | 'viewer-tuning' | null = null;
  private resizeStartX = 0;
  private resizeStartFilesWidthPx = 0;
  private resizeStartTuningWidthPx = 0;
  private boundColumnResizeMoveHandler = (event: PointerEvent) => this.onColumnResizePointerMove(event);
  private boundColumnResizeUpHandler = (event: PointerEvent) => this.onColumnResizePointerUp(event);
  private boundPreviewWheelHandler = (event: WheelEvent) => this.onPreviewWheel(event);
  isColumnResizeDragging = false;
  isColumnLayoutReady = false;
  private pendingColumnResizeFrameId: number | null = null;

  constructor(
    public router: Router,
    private route: ActivatedRoute,
    public authService: AuthService,
    private modalService: ModalService,
    private truthManagementService: TruthManagement,
    private notificationService: NotificationService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.isAuthenticated = this.authService.hasValidToken();

    this.authService.userRole$.subscribe((role) => {
      if (role !== null) {
        this.isRoleResolved = true;
      }
    });

    this.lastKnownClientId = this.authService.getCurrentClient()?.client_id ?? null;
    this.currentClientSub = this.authService.currentClient$.subscribe(client => {
      const newClientId = client?.client_id ?? null;
      if (this.lastKnownClientId && newClientId !== this.lastKnownClientId) {
        this.router.navigate(['/indexing']);
        return;
      }
      this.lastKnownClientId = newClientId;
    });

    const selectedLoanContext = this.resolveSelectedLoanContext();
    if (!selectedLoanContext) {
      this.router.navigate(['/indexing']);
      return;
    }

    this.selectedLoanContext = selectedLoanContext;
    this.loadAnnotationDocumentTypes();
    this.sourceRequestId = this.resolveSourceRequestId(selectedLoanContext);
    this.sourceFiles = [];
    this.sourceAttributes = [];
    this.selectedFileId = '';
    this.isFileColumnCollapsed = false;
    this.initializeAnnotationPanelStates();
    this.initializeSourceAttributesState();
    this.initializeDefaultColumnWidths();
    this.enforceColumnWidthBounds();
    this.isColumnLayoutReady = true;

    const sharedState = this.loadSharedDateFilterState();
    if (sharedState) {
      this.applySharedDateFilterState(sharedState);
    } else {
      const now = new Date();
      const start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      this.startDate = this.formatDateTimeForInput(start);
      this.endDate = this.formatDateTimeForInput(now);
      this.syncTimePartsFromDateTimes();
      this.syncDateDisplayValues();
      this.saveSharedDateFilterState();
    }

    this.loadSourceDataFromSequencingPoll();
  }

  ngAfterViewInit(): void {
    this.setupPreviewWheelZoom();
    // Observe the real scroll container (the CDK viewport element). The old
    // setup only ran from a dead ng2-pdf-viewer callback, so column / splitter
    // resizes never re-fit the pages.
    this.setupPreviewResizeObserver();
    queueMicrotask(() => this.scrollToPage(this.currentPage, false));
  }

  ngOnDestroy(): void {
    this.sequencingPollSub?.unsubscribe();
    this.sequencingDocumentTypesSub?.unsubscribe();
    this.sequencingExecutionDataSub?.unsubscribe();
    this.currentClientSub?.unsubscribe();
    this.clearSourceBoundingBoxHighlight();

    if (this.sourceBoundingBoxRecenterTimeout !== null) {
      clearTimeout(this.sourceBoundingBoxRecenterTimeout);
      this.sourceBoundingBoxRecenterTimeout = null;
    }

    if (this.sourceBoundingBoxReRenderTimer !== null) {
      clearTimeout(this.sourceBoundingBoxReRenderTimer);
      this.sourceBoundingBoxReRenderTimer = null;
    }

    this.previewResizeObserver?.disconnect();
    this.previewResizeObserver = null;
    this.cancelPendingPreviewZoomRestore();
    this.stopColumnResizeDrag();
    this.removePreviewWheelZoom();
    this.revokePreviewPdfObjectUrl();
    this.clearPreviewPdfSessionCache();
    this.closeAnnotationDocumentTypeDropdown();
    this.closeAnnotationApplyMenu();

    // Custom PDF.js pipeline cleanup. Page canvases are owned by the
    // PdfPageCanvasDirective instances CDK destroys with the view.
    this.warmCacheAbortController?.abort();
    this.warmCacheAbortController = null;
    if (this.previewResizeTimeout !== null) {
      clearTimeout(this.previewResizeTimeout);
      this.previewResizeTimeout = null;
    }
    try { this.sourcePdfDoc?.destroy?.(); } catch { /* ignore */ }
    this.sourcePdfDoc = null;
  }
  @HostListener('window:resize')
  onWindowResize(): void {
    if (!this.isDesktopLayout()) {
      this.stopColumnResizeDrag();
      return;
    }

    this.enforceColumnWidthBounds();

    // Debounced relayout of PDF placeholders so pages reflow after resize
    if (this.previewResizeTimeout !== null) {
      clearTimeout(this.previewResizeTimeout);
    }
    this.previewResizeTimeout = setTimeout(() => {
      this.previewResizeTimeout = null;
      if (this.sourcePdfDoc) { this.relayoutPages(); }
    }, 200);
  }


  logout(): void {
    this.modalService.showLogoutConfirmation(() => {
      this.authService.logout();
      this.isAuthenticated = false;
    });
  }

  navigateToAdminSettings(): void {
    this.router.navigate(['/admin-settings']);
  }

  navigateToStatsDashboard(): void {
    this.router.navigate(['/stats-dashboard']);
  }

  navigateToEvaluationDashboard(): void {
    this.router.navigate(['/dashboard']);
  }

  navigateToOperationsRequestAnalytics(): void {
    this.router.navigate(['/indexing']);
  }

  switchRightTab(tab: 'annotation' | 'reclassification'): void {
    this.activeRightTab = tab;
    if (tab === 'reclassification') {
      this.showAttributesSavedDot = false;
    }
  }

  canSaveAnnotationChanges(): boolean {
    if (!this.sourceRequestId || this.isAnnotationSaveInProgress) {
      return false;
    }

    return this.buildAnnotationSavePayload() !== null;
  }

  onSaveChanges(): void {
    if (this.isAnnotationSaveInProgress || !this.sourceRequestId) {
      return;
    }

    const payload = this.buildAnnotationSavePayload();
    if (!payload) {
      return;
    }

    this.isAnnotationSaveInProgress = true;
    this.truthManagementService.saveTuneAnnotations(this.sourceRequestId, payload).subscribe({
      next: () => {
        this.commitAnnotationSaveBaselineForSelectedFile();
        this.showAttributesSavedDot = true;
        this.notificationService.showSuccess('Changes are saved successfully');
        this.refreshSourceFromPoll();
      },
      error: (error) => {
        this.isAnnotationSaveInProgress = false;
      },
    });
  }

  private refreshSourceFromPoll(): void {
    if (!this.sourceRequestId) {
      this.isAnnotationSaveInProgress = false;
      return;
    }

    // Snapshot the user's position. The poll re-applies files/annotations but
    // never tears down the PDF document or the CDK viewport, so the only way
    // the view would move is if we asked it to — and selectFile() would
    // (it resets the page and scrolls to the document's start). Restore the
    // state swap only, and pin page/header/scroll to what they were.
    const previousSelectedFileId = this.selectedFileId;
    const previousPage = this.currentPage;
    const previousPageInput = this.pageInput;
    const viewport = this.previewScroller?.nativeElement ?? null;
    const previousScrollTop = viewport ? viewport.scrollTop : null;

    this.truthManagementService.getSequencingPollSequence({
      request_id: this.sourceRequestId,
    }).subscribe({
      next: (response) => {
        this.ngZone.run(() => {
          this.applySequencingPollResponse(response);
          // Everything pending was just saved; drop the per-document snapshots
          // so the restore below re-hydrates from the fresh server state.
          this.annotationPanelStateByFileId.clear();
          if (previousSelectedFileId && this.sourceFiles.some((f) => f.id === previousSelectedFileId)) {
            // applySequencingPollResponse blanked selectedFileId, so this passes
            // the same-id guard and re-runs the per-file state swap (attributes,
            // annotation rows, bbox map, tree highlight) from the fresh poll —
            // without the click-only page reset / rotation clear / scroll.
            this.activateDocumentById(previousSelectedFileId);
            this.currentPage = previousPage;
            this.pageInput = previousPageInput;
            if (viewport && previousScrollTop !== null && Math.abs(viewport.scrollTop - previousScrollTop) > 1) {
              viewport.scrollTop = previousScrollTop;
            }
          }
          this.isAnnotationSaveInProgress = false;
          this.cdr.detectChanges();
        });
      },
      error: (error) => {
        this.ngZone.run(() => {
          this.isAnnotationSaveInProgress = false;
          this.cdr.detectChanges();
        });
      },
    });
  }

  onTimeFilterChange(): void {
    this.endDateInvalid = false;
    this.dateRangeValidationMessage = '';

    if (this.selectedTimeFilter !== 'custom') {
      this.popupDirty = false;
      this.activeDateTimePopup = null;
    }

    const now = new Date();
    if (this.selectedTimeFilter === 'today') {
      const start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      this.startDate = this.formatDateTimeForInput(start);
      this.endDate = this.formatDateTimeForInput(now);
    } else if (this.selectedTimeFilter === 'last7') {
      const start = new Date(now.getTime() - 168 * 60 * 60 * 1000);
      this.startDate = this.formatDateTimeForInput(start);
      this.endDate = this.formatDateTimeForInput(now);
    } else if (this.selectedTimeFilter === 'last30') {
      const start = new Date(now.getTime() - 720 * 60 * 60 * 1000);
      this.startDate = this.formatDateTimeForInput(start);
      this.endDate = this.formatDateTimeForInput(now);
    }

    this.syncTimePartsFromDateTimes();
    this.syncDateDisplayValues();
    this.saveSharedDateFilterState();
  }

  onDateChange(): void {
    this.syncTimePartsFromDateTimes();
    this.syncDateDisplayValues();
    this.selectedTimeFilter = 'custom';

    if (this.validateDateRange()) {
      this.saveSharedDateFilterState();
    }
  }

  onDateInputChange(field: 'start' | 'end', event: Event): void {
    const input = event.target as HTMLInputElement;
    const formatted = this.normalizeDisplayDateTimeInput(input.value);

    if (field === 'start') {
      this.startDateDisplay = formatted;
    } else {
      this.endDateDisplay = formatted;
    }

    if (input.value !== formatted) {
      input.value = formatted;
    }

    this.endDateInvalid = false;
    this.dateRangeValidationMessage = '';
  }

  onDateInputKeydown(event: KeyboardEvent): void {
    const key = event.key;
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];

    if (allowedKeys.includes(key) || event.ctrlKey || event.metaKey) {
      return;
    }

    if (!/^[\d/:\s]$/.test(key) && !/^[aApPmM]$/.test(key)) {
      event.preventDefault();
    }
  }

  onDateInputBlur(field: 'start' | 'end'): void {
    const value = (field === 'start' ? this.startDateDisplay : this.endDateDisplay).trim();
    if (!value) {
      this.endDateInvalid = field === 'end';
      return;
    }

    const localDateTime = this.convertDisplayToLocalDateTime(value);
    if (!localDateTime) {
      this.endDateInvalid = field === 'end';
      return;
    }

    const currentValue = field === 'start' ? this.startDate : this.endDate;
    if (field === 'start') {
      this.startDateDisplay = this.formatDateForDisplay(localDateTime);
    } else {
      this.endDateDisplay = this.formatDateForDisplay(localDateTime);
    }

    if (localDateTime === currentValue) {
      return;
    }

    if (field === 'start') {
      this.startDate = localDateTime;
    } else {
      this.endDate = localDateTime;
    }

    this.onDateChange();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const path = (event.composedPath?.() || []) as EventTarget[];

    if (this.activeDateTimePopup) {
      const isInsidePopup = path.some((target) => {
        if (!(target instanceof HTMLElement)) {
          return false;
        }

        return (
          target.classList.contains('date-time-popup-anchor') ||
          target.classList.contains('date-time-popup-panel') ||
          target.classList.contains('mat-calendar')
        );
      });

      if (!isInsidePopup) {
        this.closeDateTimePopup();
      }
    }

    if (this.activeAnnotationDocumentTypeDropdownPage !== null) {
      const isInsideAnnotationDropdown = path.some((target) => {
        if (!(target instanceof HTMLElement)) {
          return false;
        }

        return target.classList.contains('annotation-document-type-select-wrap')
          || !!target.closest('.annotation-document-type-select-wrap');
      });

      if (!isInsideAnnotationDropdown) {
        this.closeAnnotationDocumentTypeDropdown();
      }
    }

    if (this.activeAnnotationApplyMenuPage !== null) {
      const isInsideApplyMenu = path.some((target) => {
        if (!(target instanceof HTMLElement)) {
          return false;
        }

        return target.classList.contains('annotation-apply-menu-wrap')
          || !!target.closest('.annotation-apply-menu-wrap');
      });

      if (!isInsideApplyMenu) {
        this.closeAnnotationApplyMenu();
      }
    }
  }



  @HostListener('document:keydown.escape')
  onDocumentEscape(): void {
    this.closeDateTimePopup();
    this.closeAnnotationDocumentTypeDropdown();
    this.closeAnnotationApplyMenu();
  }

  openDateTimePopup(field: 'start' | 'end', event?: Event): void {
    event?.stopPropagation();

    if (this.activeDateTimePopup && this.activeDateTimePopup !== field) {
      this.closeDateTimePopup();
    }

    this.activeDateTimePopup = field;
    this.popupDirty = false;

    const source = field === 'start' ? this.startDate : this.endDate;
    const parsed = this.parseLocalDateTime(source);
    if (parsed) {
      this.popupDate = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
      const isPm = parsed.getHours() >= 12;
      this.popupPeriod = isPm ? 'PM' : 'AM';
      this.popupHour12 = String(parsed.getHours() % 12 || 12).padStart(2, '0');
      this.popupMinute = String(parsed.getMinutes()).padStart(2, '0');
      this.removeCalendarNavTitles();
      return;
    }

    const now = new Date();
    this.popupDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    this.popupHour12 = '12';
    this.popupMinute = '00';
    this.popupPeriod = 'AM';
    this.removeCalendarNavTitles();
  }

  onPopupPanelClick(event: MouseEvent): void {
    event.stopPropagation();
    this.removeCalendarNavTitles();
  }

  closeDateTimePopup(event?: Event): void {
    event?.stopPropagation();
    if (this.popupDirty) {
      this.onDateChange();
    }
    this.activeDateTimePopup = null;
    this.popupDirty = false;
  }

  onPopupDateSelected(date: Date | null): void {
    if (!date) {
      return;
    }

    this.popupDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    this.commitPopupSelection();
  }

  onPopupTimePartChange(): void {
    this.commitPopupSelection();
  }

  getPopupMinDate(field: 'start' | 'end'): Date | null {
    if (field === 'start') {
      return null;
    }

    const start = this.parseLocalDateTime(this.startDate);
    return start ? new Date(start.getFullYear(), start.getMonth(), start.getDate()) : null;
  }

  getPopupMaxDate(field: 'start' | 'end'): Date | null {
    if (field === 'end') {
      return null;
    }

    return this.getTodayDateForPopup();
  }

  getTodayDateForPopup(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  isPopupOpen(field: 'start' | 'end'): boolean {
    return this.activeDateTimePopup === field;
  }

  selectFile(fileId: string): void {
    const isDifferentFileSelection = this.selectedFileId !== fileId;

    // Resolve the target page from the CLICKED file, not from whatever is
    // currently selected — toAbsolutePreviewPage() reads the current selection,
    // which is the previous file until activateDocumentById runs below.
    const targetFile = this.sourceFiles.find((f) => f.id === fileId);
    const startPage = (
      typeof targetFile?.startPageNumber === 'number'
      && Number.isFinite(targetFile.startPageNumber)
      && targetFile.startPageNumber > 0
    ) ? targetFile.startPageNumber : 1;

    if (isDifferentFileSelection) {
      // Activate FIRST so every "current file" lookup (annotation sync,
      // attribute restore, toAbsolutePreviewPage) resolves against the clicked
      // file. activateDocumentById stays the single funnel; scrollToPage's
      // later scroll-sync call hits its same-id guard and is a no-op.
      this.activateDocumentById(fileId);

      // Click-only view resets (the scroll-driven path deliberately keeps
      // currentPage / rotations).
      this.pageRotations.clear();
      this.currentPage = startPage;
      this.pageInput = startPage;
    } else {
      // Same-file re-click: preserve existing "soft-reset current view"
      // behavior (clear bbox highlight, re-sync annotation pages, re-clone
      // the field-source-data map from the pre-built per-file cache). A
      // same-doc re-click means "reset current view", not "scroll to start".
      this.clearSourceBoundingBoxHighlight();
      this.syncAnnotationPagesForSelectedFile();
      this.restoreAttributeStateForFile(fileId);
      this.sourceFieldSourceDataMap = new Map(this.sourceFieldSourceDataByFileId.get(fileId) || []);
    }

    // Custom PDF.js pipeline: load the source PDF once for the session, then
    // just scroll to this file's absolute start page. `loadSourcePdf` is
    // idempotent (checked via `sourcePdfDoc` presence).
    if (!this.sourcePdfDoc) {
      this.loadSourcePdf().then(() => {
        if (!this.sourcePdfDoc) { return; }
        // Boot-order rule: loadPdf().then(jumpToLandingPage) — honour a
        // ?page=N query param when present so a deep-link lands on the
        // target page instead of the file's start.
        const deepRaw = new URLSearchParams(window.location.search).get('page');
        const deep = deepRaw !== null ? Number(deepRaw) : NaN;
        const target = (Number.isFinite(deep) && deep >= 1 && deep <= this.totalPages)
          ? Math.floor(deep)
          : startPage;
        this.scrollToPage(target, false);
      });
    } else if (isDifferentFileSelection) {
      this.scrollToPage(startPage, false);
    }
  }

  toggleFileColumn(): void {
    if (!this.selectedFileId) {
      return;
    }

    if (!this.isFileColumnCollapsed && this.collapsedPreviewWidthPx === null) {
      this.captureCollapsedPreviewWidth();
    }

    this.isFileColumnCollapsed = !this.isFileColumnCollapsed;

    if (this.isFileColumnCollapsed) {
      this.stopColumnResizeDrag();
      this.enforceColumnWidthBounds();
    } else {
      this.enforceColumnWidthBounds();
    }

    this.dispatchSourceResizeEventsDuringTransition();
  }

  isAnyColumnResizeEnabled(): boolean {
    return this.isDesktopLayout();
  }

  canResizeSplitter(splitter: 'files-viewer' | 'viewer-tuning'): boolean {
    if (!this.isDesktopLayout()) {
      return false;
    }

    if (splitter === 'files-viewer') {
      return !this.isFileColumnCollapsed;
    }

    return true;
  }

  getContentGridStyles(): { [key: string]: string } {
    if (!this.isDesktopLayout()) {
      return {};
    }

    if (this.isFileColumnCollapsed) {
      const contentWidth = this.getContentGridTrackSpaceWidth(4);
      const collapsedTuningWidthPx = this.getCollapsedTuningWidthForLockedPreview(contentWidth);
      return {
        'grid-template-columns': `${this.collapsedFilesColumnWidthPx}px minmax(${this.minViewerColumnWidthPx}px, 1fr) ${this.splitterWidthPx}px ${collapsedTuningWidthPx}px`,
      };
    }

    return {
      'grid-template-columns': `${this.filesColumnWidthPx}px ${this.splitterWidthPx}px minmax(${this.minViewerColumnWidthPx}px, 1fr) ${this.splitterWidthPx}px ${this.tuningColumnWidthPx}px`,
    };
  }

  startColumnResize(event: PointerEvent, splitter: 'files-viewer' | 'viewer-tuning'): void {
    if (!this.canResizeSplitter(splitter)) {
      return;
    }

    event.preventDefault();

    const gridWidth = this.getContentGridWidth();
    if (gridWidth <= 0) {
      return;
    }

    this.activeColumnSplitter = splitter;
    this.isColumnResizeDragging = true;
    this.hasUserCustomizedColumnWidths = true;
    this.resizeStartX = event.clientX;
    this.resizeStartFilesWidthPx = this.filesColumnWidthPx;
    this.resizeStartTuningWidthPx = this.tuningColumnWidthPx;

    window.addEventListener('pointermove', this.boundColumnResizeMoveHandler);
    window.addEventListener('pointerup', this.boundColumnResizeUpHandler);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  }

  private onColumnResizePointerMove(event: PointerEvent): void {
    if (!this.activeColumnSplitter || !this.canResizeSplitter(this.activeColumnSplitter)) {
      return;
    }

    if (this.activeColumnSplitter === 'viewer-tuning' && this.isFileColumnCollapsed && this.collapsedPreviewWidthPx !== null) {
      this.collapsedPreviewWidthPx = null;
    }

    const deltaX = event.clientX - this.resizeStartX;
    const trackCount = this.isFileColumnCollapsed ? 4 : 5;
    const contentWidth = this.getContentGridTrackSpaceWidth(trackCount);
    if (contentWidth <= 0) {
      return;
    }

    const splitterTotal = this.splitterWidthPx * 2;

    if (this.activeColumnSplitter === 'files-viewer') {
      const maxFilesWidth = Math.max(
        this.minFilesColumnWidthPx,
        contentWidth - splitterTotal - this.minViewerColumnWidthPx - this.tuningColumnWidthPx,
      );
      const nextFilesWidth = this.resizeStartFilesWidthPx + deltaX;
      this.filesColumnWidthPx = this.clamp(nextFilesWidth, this.minFilesColumnWidthPx, maxFilesWidth);
    } else {
      const maxTuningWidth = this.isFileColumnCollapsed
        ? Math.max(
          this.minTuningColumnWidthPx,
          contentWidth - this.collapsedFilesColumnWidthPx - this.splitterWidthPx - this.minViewerColumnWidthPx,
        )
        : Math.max(
          this.minTuningColumnWidthPx,
          contentWidth - splitterTotal - this.minViewerColumnWidthPx - this.filesColumnWidthPx,
        );
      const nextTuningWidth = this.resizeStartTuningWidthPx - deltaX;
      this.tuningColumnWidthPx = this.clamp(nextTuningWidth, this.minTuningColumnWidthPx, maxTuningWidth);
    }

    this.queueColumnResizeRender();
  }

  private onColumnResizePointerUp(_event: PointerEvent): void {
    this.stopColumnResizeDrag();
    this.dispatchSourceResizeEvent();
    this.cdr.detectChanges();
  }

  private stopColumnResizeDrag(): void {
    this.activeColumnSplitter = null;
    this.isColumnResizeDragging = false;
    if (this.pendingColumnResizeFrameId !== null) {
      cancelAnimationFrame(this.pendingColumnResizeFrameId);
      this.pendingColumnResizeFrameId = null;
    }
    window.removeEventListener('pointermove', this.boundColumnResizeMoveHandler);
    window.removeEventListener('pointerup', this.boundColumnResizeUpHandler);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }

  private queueColumnResizeRender(): void {
    if (this.pendingColumnResizeFrameId !== null) {
      return;
    }

    this.pendingColumnResizeFrameId = requestAnimationFrame(() => {
      this.pendingColumnResizeFrameId = null;
      this.cdr.detectChanges();
    });
  }

  private enforceColumnWidthBounds(): void {
    if (!this.isDesktopLayout()) {
      return;
    }

    const trackCount = this.isFileColumnCollapsed ? 4 : 5;
    const contentWidth = this.getContentGridTrackSpaceWidth(trackCount);
    if (contentWidth <= 0) {
      return;
    }

    if (this.isFileColumnCollapsed) {
      this.tuningColumnWidthPx = this.getCollapsedTuningWidthForLockedPreview(contentWidth);
      return;
    }

    const splitterTotal = this.splitterWidthPx * 2;
    const totalMinRequired = this.minFilesColumnWidthPx + this.minViewerColumnWidthPx + this.minTuningColumnWidthPx + splitterTotal;
    if (contentWidth <= totalMinRequired) {
      this.filesColumnWidthPx = this.minFilesColumnWidthPx;
      this.tuningColumnWidthPx = this.minTuningColumnWidthPx;
      return;
    }

    const maxFilesWidth = contentWidth - splitterTotal - this.minViewerColumnWidthPx - this.minTuningColumnWidthPx;
    this.filesColumnWidthPx = this.clamp(this.filesColumnWidthPx, this.minFilesColumnWidthPx, maxFilesWidth);

    const maxTuningWidth = contentWidth - splitterTotal - this.minViewerColumnWidthPx - this.filesColumnWidthPx;
    this.tuningColumnWidthPx = this.clamp(this.tuningColumnWidthPx, this.minTuningColumnWidthPx, maxTuningWidth);
  }

  private initializeDefaultColumnWidths(): void {
    if (!this.isDesktopLayout() || this.hasUserCustomizedColumnWidths) {
      return;
    }

    const contentWidth = this.getContentGridTrackSpaceWidth(5);
    if (contentWidth <= 0) {
      return;
    }

    const availableWidth = Math.max(0, contentWidth - (this.splitterWidthPx * 2));
    this.filesColumnWidthPx = Math.round(availableWidth * this.defaultFilesRatio);
    this.tuningColumnWidthPx = Math.round(availableWidth * this.defaultTuningRatio);
  }

  private getContentGridTrackSpaceWidth(trackCount: number): number {
    const gridWidth = this.getContentGridWidth();
    if (gridWidth <= 0) {
      return 0;
    }

    const gapTotal = this.getContentGridGapTotalPx(trackCount);
    return Math.max(0, gridWidth - gapTotal);
  }

  private getContentGridGapTotalPx(trackCount: number): number {
    const gaps = Math.max(0, trackCount - 1);
    return this.getContentGridColumnGapPx() * gaps;
  }

  private getContentGridColumnGapPx(): number {
    const gridElement = this.contentGrid?.nativeElement;
    if (!gridElement) {
      return 8;
    }

    const computed = window.getComputedStyle(gridElement);
    const gapValue = Number.parseFloat(computed.columnGap || computed.gap || '8');
    return Number.isFinite(gapValue) ? gapValue : 8;
  }

  private getContentGridWidth(): number {
    const elementWidth = this.contentGrid?.nativeElement?.clientWidth || 0;
    if (elementWidth > 0) {
      return elementWidth;
    }

    return Math.max(0, window.innerWidth - 32);
  }

  private captureCollapsedPreviewWidth(): void {
    if (!this.isDesktopLayout() || this.isFileColumnCollapsed) {
      return;
    }

    const contentWidth = this.getContentGridTrackSpaceWidth(5);
    if (contentWidth <= 0) {
      return;
    }

    const previewWidth = contentWidth - this.filesColumnWidthPx - (this.splitterWidthPx * 2) - this.tuningColumnWidthPx;
    this.collapsedPreviewWidthPx = Math.max(this.minViewerColumnWidthPx, Math.round(previewWidth));
  }

  private getCollapsedTuningWidthForLockedPreview(contentWidth: number): number {
    if (contentWidth <= 0) {
      return this.tuningColumnWidthPx;
    }

    const maxTuningWidth = Math.max(
      this.minTuningColumnWidthPx,
      contentWidth - this.collapsedFilesColumnWidthPx - this.splitterWidthPx - this.minViewerColumnWidthPx,
    );

    if (this.collapsedPreviewWidthPx === null) {
      return this.clamp(this.tuningColumnWidthPx, this.minTuningColumnWidthPx, maxTuningWidth);
    }

    const targetTuningWidth = contentWidth - this.collapsedFilesColumnWidthPx - this.splitterWidthPx - this.collapsedPreviewWidthPx;
    return this.clamp(targetTuningWidth, this.minTuningColumnWidthPx, maxTuningWidth);
  }

  private isDesktopLayout(): boolean {
    return window.innerWidth > this.desktopBreakpointPx;
  }

  private dispatchSourceResizeEvent(): void {
    window.dispatchEvent(new Event('resize'));
  }

  private dispatchSourceResizeEventsDuringTransition(): void {
    const resizeEvent = new Event('resize');
    const transitionDuration = 400;
    const intervals = 8;
    const intervalTime = transitionDuration / intervals;

    for (let i = 1; i <= intervals; i += 1) {
      setTimeout(() => {
        window.dispatchEvent(resizeEvent);
      }, i * intervalTime);
    }

    setTimeout(() => {
      window.dispatchEvent(resizeEvent);
    }, transitionDuration + 50);
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  getFileNumber(file: SourceFileItem): number {
    return this.sourceFiles.indexOf(file) + 1;
  }

  isMiscellaneousFile(file: SourceFileItem): boolean {
    return (file.fileName ?? '').trim().toUpperCase() === 'MISCELLANEOUS';
  }

  get totalDocuments(): number {
    return this.sourceFiles.length;
  }

  get totalPageCount(): number {
    return this.sourceFiles.reduce((sum, f) => sum + (f.totalDocumentPages ?? 0), 0);
  }

  get miscellaneousCount(): number {
    return this.sourceFiles.filter((f) => this.isMiscellaneousFile(f)).length;
  }

  get filteredSourceFiles(): SourceFileItem[] {
    const base = this.fileFilterKind === 'misc'
      ? this.sourceFiles.filter((f) => this.isMiscellaneousFile(f))
      : this.sourceFiles;

    const term = this.fileSearchTerm.trim().toLowerCase();
    if (!term) {
      return base;
    }

    return base.filter((file) => {
      return (
        file.fileName.toLowerCase().includes(term)
        || file.subFileName.toLowerCase().includes(term)
        || file.version.toLowerCase().includes(term)
        || file.matchTypeLabel.toLowerCase().includes(term)
        || file.templateId.toLowerCase().includes(term)
      );
    });
  }

  getSourceFilesEmptyMessage(): string {
    if (this.isSourceDataLoading) {
      return 'Loading files...';
    }

    if (this.fileSearchTerm.trim()) {
      return 'No files match your search.';
    }

    if (this.fileFilterKind === 'misc') {
      return 'No miscellaneous files.';
    }

    return this.sourceDataMessage || 'No files available.';
  }

  get selectedSourceFile(): SourceFileItem | null {
    if (!this.selectedFileId) {
      return null;
    }

    return this.sourceFiles.find((file) => file.id === this.selectedFileId) ?? null;
  }

  onFileAction(event: Event): void {
    event.stopPropagation();
  }

  previousPage(): void {
    this.setCurrentPage(this.currentPage - 1, true);
  }

  nextPage(): void {
    this.setCurrentPage(this.currentPage + 1, true);
  }

  goToPage(): void {
    const parsedPage = Number.parseInt(String(this.pageInput), 10);
    if (Number.isNaN(parsedPage)) {
      this.pageInput = this.currentPage;
      return;
    }

    this.setCurrentPage(parsedPage, true);
  }

  onPageInputKeydown(event: KeyboardEvent): void {
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];

    if (allowedKeys.includes(event.key)) {
      if (event.key === 'Enter') {
        this.goToPage();
      }
      return;
    }

    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  zoomIn(): void {
    if (!this.sourcePdfDoc) {
      return;
    }

    this.applyZoomWithCenterAnchor(this.zoom + 0.25);
  }

  zoomOut(): void {
    if (!this.sourcePdfDoc) {
      return;
    }

    this.applyZoomWithCenterAnchor(this.zoom - 0.25);
  }

  setZoomByPercentage(): void {
    const parsedZoom = Number.parseInt(String(this.zoomPercentage), 10);
    if (Number.isNaN(parsedZoom)) {
      this.zoomPercentage = Math.round(this.zoom * 100);
      return;
    }

    const normalizedZoom = Math.min(300, Math.max(50, parsedZoom));
    this.applyZoomWithCenterAnchor(normalizedZoom / 100);
  }

  onZoomInputKeydown(event: KeyboardEvent): void {
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];

    if (allowedKeys.includes(event.key)) {
      if (event.key === 'Enter') {
        this.setZoomByPercentage();
      }
      return;
    }

    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  rotatePdf(): void {
    // `pdfSrc` is the legacy ng2-pdf-viewer source and is never set by the
    // custom pipeline — guard on the pdf.js document instead.
    if (!this.sourcePdfDoc) {
      return;
    }

    const nextRotation = (this.pageRotations.get(this.currentPage) ?? 0) + 90;
    this.pageRotations.set(this.currentPage, nextRotation);
    // The [userRotation] binding on the page's canvas directive picks this up and
    // applies the same animated CSS transform the old applyPreviewPageRotation did.
    this.cdr.markForCheck();
  }

  onPreviewWheel(event: WheelEvent): void {
    if (!event.ctrlKey) {
      return;
    }

    event.preventDefault();

    if (!this.sourcePdfDoc) {
      return;
    }

    const viewer = this.getPdfViewerScrollContainer();
    if (!viewer) {
      return;
    }

    const viewerRect = viewer.getBoundingClientRect();
    const cursorX = this.clamp(event.clientX - viewerRect.left, 0, viewer.clientWidth);
    const cursorY = this.clamp(event.clientY - viewerRect.top, 0, viewer.clientHeight);
    // ±5% per notch — same step as the Prompt Tuning page. updateZoom clamps to
    // min/max and no-ops when unchanged, so the limits behave identically.
    const delta = event.deltaY > 0 ? -0.05 : 0.05;

    this.applyZoomWithAnchor(this.zoom + delta, cursorX, cursorY);
  }

  onPreviewPageChange(page: number): void {
    this.currentPage = page;
    this.pageInput = page;
    this.syncSelectedFileToVisiblePage(page);
  }

  /**
   * When the user scrolls the PDF preview, auto-select the Mapped Document
   * whose page range contains the currently-visible page. Thin wrapper over
   * activateDocumentById — the reference implementation's rule: "clicks and
   * scrolls both go through the same wire" (single source of truth). Skips
   * the pieces that would fight the scroll (currentPage reset, rotation
   * clear, PDF reload) — see selectFile() for those.
   */
  private syncSelectedFileToVisiblePage(absolutePage: number): void {
    if (!Number.isFinite(absolutePage) || absolutePage < 1) { return; }

    const doc = this.sourceFiles.find((f) =>
      typeof f.startPageNumber === 'number' &&
      typeof f.endPageNumber === 'number' &&
      absolutePage >= f.startPageNumber &&
      absolutePage <= f.endPageNumber
    );
    if (!doc) { return; }
    this.activateDocumentById(doc.id);
  }

  /**
   * The one path that "activates" a mapped document — the per-doc state swap
   * that both sidebar clicks (selectFile) and scroll-driven doc changes
   * (syncSelectedFileToVisiblePage) funnel through.
   *
   * Honours the reference implementation's guards:
   *   - `fileId === selectedFileId` → no-op (avoids re-cloning the field-
   *     source-data map on every scroll settle within the same doc).
   *   - Bumps docActivationSeq so any async follow-up (RAF tree scroll) can
   *     drop itself when superseded by a faster activation.
   *
   * The per-file Maps built once in applySequencingPollResponse
   * (sourceFieldSourceDataByFileId, sourceFileAttributeStateByFileId) act as
   * the reference's `docDataCache` — pre-populated instead of lazily filled.
   * If a per-doc endpoint is added later, wrap the read below in an await
   * that first fills the Map on cache miss.
   */
  private activateDocumentById(fileId: string): void {
    if (!fileId || fileId === this.selectedFileId) { return; }

    const seq = ++this.docActivationSeq;

    if (this.selectedFileId) {
      this.persistCurrentFileAttributeState();
      this.persistCurrentAnnotationPanelState();
    }
    this.selectedFileId = fileId;
    this.clearSourceBoundingBoxHighlight();
    this.syncAnnotationPagesForSelectedFile();
    this.restoreAttributeStateForFile(fileId);
    this.sourceFieldSourceDataMap = new Map(this.sourceFieldSourceDataByFileId.get(fileId) || []);
    this.cdr.markForCheck();

    // Bring the newly-selected doc's row into view in the file tree so the
    // user sees which doc is highlighted. Dropped if a newer activation has
    // already superseded us (reference's dataRequestSeq stale-drop).
    requestAnimationFrame(() => {
      if (seq !== this.docActivationSeq) { return; }
      this.scrollSelectedFileIntoView();
    });
  }

  /**
   * Bring the currently-selected file's row into view in the left-column
   * file tree. No-op if it's already fully visible so we don't fight
   * user-initiated scroll of the tree.
   */
  private scrollSelectedFileIntoView(): void {
    const treeItem = document.querySelector<HTMLElement>('.file-tree .tree-item.file-item.selected');
    if (!treeItem) { return; }
    const tree = treeItem.closest<HTMLElement>('.file-tree');
    if (!tree) { return; }
    const treeRect = tree.getBoundingClientRect();
    const itemRect = treeItem.getBoundingClientRect();
    if (itemRect.top < treeRect.top || itemRect.bottom > treeRect.bottom) {
      treeItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  hasSourceAttributes(): boolean {
    return this.sourceAttributes.length > 0;
  }

  goToAnnotationPage(page: number): void {
    this.setCurrentPage(this.toAbsolutePreviewPage(page), true);
    this.toggleAnnotationPanel(page);
  }

  isAnnotationPageCurrent(localPage: number): boolean {
    return this.toAbsolutePreviewPage(localPage) === this.currentPage;
  }

  toAbsolutePreviewPage(localPage: number): number {
    const startPage = this.selectedSourceFile?.startPageNumber;
    if (typeof startPage === 'number' && Number.isFinite(startPage) && startPage > 0) {
      return startPage + localPage - 1;
    }

    return localPage;
  }

  isAnnotationPanelOpen(page: number): boolean {
    return this.expandedAnnotationPages.has(page);
  }

  getAnnotationPanelState(page: number): AnnotationPanelState {
    const existingState = this.annotationPanelStateByPage[page];
    if (existingState) {
      return existingState;
    }

    const defaultState = this.getDefaultAnnotationPanelState(page);
    this.annotationPanelStateByPage[page] = defaultState;
    return defaultState;
  }

  toggleDocumentNameEdit(page: number): void {
    const panelState = this.getAnnotationPanelState(page);
    panelState.isDocumentNameEditable = !panelState.isDocumentNameEditable;
  }

  getAnnotationDocumentTypeOptions(page: number): string[] {
    void page;
    return this.annotationDocumentTypeOptions;
  }

  isAnnotationDocumentTypeDropdownOpen(page: number): boolean {
    return this.activeAnnotationDocumentTypeDropdownPage === page;
  }

  openAnnotationDocumentTypeDropdown(page: number, event?: Event): void {
    event?.stopPropagation();

    if (this.activeAnnotationDocumentTypeDropdownPage === page) {
      return;
    }

    // The dropdown and the kebab Apply menu both open beneath the row; never
    // let them coexist (both triggers stop propagation, so the outside-click
    // handler can't close the other one).
    this.closeAnnotationApplyMenu();

    this.activeAnnotationDocumentTypeDropdownPage = page;
    this.annotationDocumentTypeSearchByPage[page] = '';
    this.loadAnnotationDocumentTypes({ force: true });
    this.updateAnnotationDocumentTypeDropdownStyle(page);
    this.attachAnnotationDocumentTypeDropdownReposition();
  }

  toggleAnnotationDocumentTypeDropdown(page: number, event?: Event): void {
    event?.stopPropagation();

    if (this.isAnnotationDocumentTypeDropdownOpen(page)) {
      this.closeAnnotationDocumentTypeDropdown();
      return;
    }

    this.openAnnotationDocumentTypeDropdown(page);
  }

  closeAnnotationDocumentTypeDropdown(): void {
    this.activeAnnotationDocumentTypeDropdownPage = null;
    this.annotationDocumentTypeDropdownStyle = null;
    this.detachAnnotationDocumentTypeDropdownReposition();
  }

  private updateAnnotationDocumentTypeDropdownStyle(page: number): void {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }
    const trigger = document.getElementById('doc-name-' + page);
    if (!trigger) {
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const gap = 4;
    const margin = 8;
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    const preferredWidth = Math.max(rect.width, 288);
    const width = Math.min(preferredWidth, viewportW - margin * 2);

    const rawLeft = Math.round(rect.left);
    const maxLeft = Math.round(viewportW - width - margin);
    const left = Math.max(margin, Math.min(rawLeft, maxLeft));

    const style: Record<string, string> = {
      left: `${left}px`,
      width: `${Math.round(width)}px`,
    };

    const panelHeight = 320;
    const openAbove = rect.bottom + gap + panelHeight > viewportH && rect.top - gap > panelHeight;

    if (!openAbove) {
      const top = Math.min(
        Math.max(Math.round(rect.bottom + gap), margin),
        Math.max(margin, viewportH - margin - panelHeight),
      );
      style['top'] = `${top}px`;
    } else {
      const bottom = Math.min(
        Math.max(Math.round(viewportH - rect.top + gap), margin),
        Math.max(margin, viewportH - margin - panelHeight),
      );
      style['bottom'] = `${bottom}px`;
    }

    this.annotationDocumentTypeDropdownStyle = style;
  }

  private readonly onAnnotationDocumentTypeDropdownReposition = (): void => {
    const page = this.activeAnnotationDocumentTypeDropdownPage;
    if (page === null) {
      return;
    }
    this.updateAnnotationDocumentTypeDropdownStyle(page);
  };

  private attachAnnotationDocumentTypeDropdownReposition(): void {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }
    document.addEventListener('scroll', this.onAnnotationDocumentTypeDropdownReposition, true);
    window.addEventListener('resize', this.onAnnotationDocumentTypeDropdownReposition);
  }

  private detachAnnotationDocumentTypeDropdownReposition(): void {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }
    document.removeEventListener('scroll', this.onAnnotationDocumentTypeDropdownReposition, true);
    window.removeEventListener('resize', this.onAnnotationDocumentTypeDropdownReposition);
  }

  isAnnotationApplyMenuOpen(page: number): boolean {
    return this.activeAnnotationApplyMenuPage === page;
  }

  toggleAnnotationApplyMenu(page: number, event?: Event): void {
    event?.stopPropagation();
    const willOpen = this.activeAnnotationApplyMenuPage !== page;
    if (willOpen) {
      // Mirror of openAnnotationDocumentTypeDropdown: the fixed-position
      // dropdown (z 1200) would otherwise sit on top of this menu (z 40).
      this.closeAnnotationDocumentTypeDropdown();
    }
    this.activeAnnotationApplyMenuPage = willOpen ? page : null;
  }

  closeAnnotationApplyMenu(): void {
    this.activeAnnotationApplyMenuPage = null;
  }

  selectAnnotationApplyDirection(page: number, direction: 'all' | 'up' | 'down', event?: Event): void {
    this.setAnnotationApplyDirection(page, direction, event);
    // Intentionally do NOT close the menu here — the user often toggles a direction on/off
    // or switches between up/down/all. The menu closes via the ⋮ toggle, an outside click,
    // ESC, or opening the document-name dropdown (all still wired up).
  }

  onSelectAddComment(page: number, event?: Event): void {
    this.closeAnnotationApplyMenu();
    this.openCommentModal(page, event);
  }

  onAnnotationDocumentTypeSearchInput(page: number, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.annotationDocumentTypeSearchByPage[page] = value;

    if (!this.isAnnotationDocumentTypeDropdownOpen(page)) {
      this.openAnnotationDocumentTypeDropdown(page);
    }
  }

  getAnnotationDocumentTypeSearchText(page: number): string {
    if (this.isAnnotationDocumentTypeDropdownOpen(page)) {
      return this.annotationDocumentTypeSearchByPage[page] ?? this.getAnnotationPanelState(page).documentName;
    }

    return this.getAnnotationPanelState(page).documentName;
  }

  getFilteredAnnotationDocumentTypeOptions(page: number): string[] {
    const query = (this.annotationDocumentTypeSearchByPage[page] ?? '').trim();
    const options = this.getAnnotationDocumentTypeOptions(page);
    if (!query) {
      return options;
    }

    const loweredQuery = query.toLowerCase();

    return options.filter((option) => option.toLowerCase().includes(loweredQuery));
  }

  selectAnnotationDocumentType(page: number, value: string, event?: Event): void {
    event?.stopPropagation();

    const panelState = this.getAnnotationPanelState(page);
    panelState.documentName = value;
    this.annotationDocumentTypeSearchByPage[page] = value;
    this.closeAnnotationDocumentTypeDropdown();
  }

  clearAnnotationDocumentTypeSearch(page: number, event: Event): void {
    event.stopPropagation();
    this.annotationDocumentTypeSearchByPage[page] = '';
    if (!this.isAnnotationDocumentTypeDropdownOpen(page)) {
      this.openAnnotationDocumentTypeDropdown(page);
    }
  }

  canApplyAnnotationUp(page: number): boolean {
    return this.isMiddleAnnotationPage(page);
  }

  canApplyAnnotationDown(page: number): boolean {
    return this.isMiddleAnnotationPage(page);
  }

  onAnnotationApplyToggle(page: number, direction: 'all' | 'up' | 'down', event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const panelState = this.getAnnotationPanelState(page);

    this.setAnnotationApplyChecked(panelState, direction, checked);

    if (!checked) {
      this.restoreAnnotationDocumentNameFromBackup(page, direction);
      this.setAnnotationApplyBackup(panelState, direction, null);
      return;
    }

    this.clearOtherAnnotationApplySelections(page, panelState, direction);

    const backup = this.captureAnnotationDocumentNameBackup(page, direction);
    this.setAnnotationApplyBackup(panelState, direction, backup);
    this.applyDocumentNameFromPage(page, direction);
  }

  getSelectedApplyDirection(page: number): 'all' | 'up' | 'down' | '' {
    const state = this.annotationPanelStateByPage[page];
    if (!state) { return ''; }
    if (state.applyToAll) { return 'all'; }
    if (state.applyUp) { return 'up'; }
    if (state.applyDown) { return 'down'; }
    return '';
  }

  isPageAppliedTarget(page: number): boolean {
    if (this.getSelectedApplyDirection(page)) { return true; }

    for (const [sourceKey, state] of Object.entries(this.annotationPanelStateByPage)) {
      const sourcePage = Number.parseInt(sourceKey, 10);
      if (Number.isNaN(sourcePage) || sourcePage === page) { continue; }

      const direction: 'all' | 'up' | 'down' | null =
        state.applyToAll ? 'all' : state.applyUp ? 'up' : state.applyDown ? 'down' : null;
      if (!direction) { continue; }

      const targets = this.getAnnotationApplyTargetPages(sourcePage, direction);
      if (targets.includes(page)) { return true; }
    }
    return false;
  }

  setAnnotationApplyDirection(page: number, direction: 'all' | 'up' | 'down', event?: Event): void {
    event?.stopPropagation();

    if (direction === 'up' && !this.canApplyAnnotationUp(page)) { return; }
    if (direction === 'down' && !this.canApplyAnnotationDown(page)) { return; }

    const currentDirection = this.getSelectedApplyDirection(page);

    if (currentDirection === direction) {
      this.onAnnotationApplyToggle(page, direction, { target: { checked: false } } as unknown as Event);
      return;
    }

    if (currentDirection) {
      this.onAnnotationApplyToggle(page, currentDirection, { target: { checked: false } } as unknown as Event);
    }

    this.onAnnotationApplyToggle(page, direction, { target: { checked: true } } as unknown as Event);
  }

  hasCommentForPage(page: number): boolean {
    const state = this.annotationPanelStateByPage[page];
    return !!state && typeof state.comments === 'string' && state.comments.trim().length > 0;
  }

  openCommentModal(page: number, event?: Event): void {
    event?.stopPropagation();
    const state = this.getAnnotationPanelState(page);
    const hasExisting = state.comments && state.comments.trim().length > 0;
    this.modalService.showPrompt(
      '',
      (value: string) => {
        this.getAnnotationPanelState(page).comments = (value || '').trimEnd();
      },
      {
        title: `Comment for P${page}`,
        placeholder: 'Type your comment…',
        confirmText: hasExisting ? 'Save' : 'Add',
        cancelText: 'Cancel',
        initialValue: state.comments || '',
        inputMultiline: true,
        inputRows: 4,
        requireInputChange: true,
      },
    );
  }

  get filteredSourceAttributes(): SourceAttributeItem[] {
    const term = this.attributeSearchTerm.trim().toLowerCase();
    if (!term) {
      return this.sourceAttributes;
    }

    return this.sourceAttributes.filter((attribute) => {
      return attribute.attributeLabel.toLowerCase().includes(term) || attribute.description.toLowerCase().includes(term);
    });
  }

  getSourceAttributesEmptyMessage(): string {
    if (this.isSourceDataLoading) {
      return 'Loading attributes...';
    }

    if (this.attributeSearchTerm.trim()) {
      return 'No matching attributes found';
    }

    return this.sourceDataMessage || 'No attributes available for this file.';
  }

  getSourceAttributesEmptySubtitle(): string {
    if (this.attributeSearchTerm.trim()) {
      return 'Try a different search term';
    }

    if (!this.sourceRequestId) {
      return 'Open Source from Indexing to load sequencing data.';
    }

    return '';
  }

  getSourceDisplayValueAsArray(attributeId: number): string[] {
    const attribute = this.getSourceAttribute(attributeId);
    if (!attribute) {
      return [''];
    }

    const values = this.sourceAttributeValues[attributeId] ?? [''];
    if (values.length > 0) {
      return values;
    }

    return attribute.isArray ? [''] : [''];
  }

  getSourceDisplayValue(attributeId: number): string {
    const values = this.getSourceDisplayValueAsArray(attributeId);
    return values[0] ?? '';
  }

  getSourceValueIndicator(attributeId: number): string {
    if (this.isSourceNotApplicable(attributeId)) {
      return 'N/A';
    }

    if (this.isSourceValueInvalid(attributeId)) {
      return '?';
    }

    const hasValue = this.getSourceDisplayValueAsArray(attributeId).some((value) => value.trim().length > 0);
    return hasValue ? '✓' : '○';
  }

  getSourceValueIndicatorState(attributeId: number): 'valid' | 'invalid' | 'na' | 'neutral' {
    if (this.isSourceNotApplicable(attributeId)) {
      return 'na';
    }

    if (this.isSourceValueInvalid(attributeId)) {
      return 'invalid';
    }

    const hasValue = this.getSourceDisplayValueAsArray(attributeId).some((value) => value.trim().length > 0);
    return hasValue ? 'valid' : 'neutral';
  }

  getSourceFieldType(attributeId: number): SourceAttributeFieldType {
    return this.getSourceAttribute(attributeId)?.fieldType ?? 'text';
  }

  getSourceFieldAllowedValuesWithDescriptions(attributeId: number): SourceAttributeOption[] {
    return this.getSourceAttribute(attributeId)?.allowedValues ?? [];
  }

  getSourceSelectDisplayLabel(attributeId: number, value: string): string {
    const option = this.getSourceFieldAllowedValuesWithDescriptions(attributeId).find((item) => item.value === value);
    return option?.displayText ?? value;
  }

  isSourceNotApplicable(attributeId: number): boolean {
    return this.sourceNotApplicableAttributes.has(attributeId);
  }

  isSourceModified(attributeId: number): boolean {
    const currentValues = this.sourceAttributeValues[attributeId] ?? [];
    const originalValues = this.sourceOriginalAttributeValues[attributeId] ?? [];
    const valuesChanged = !this.areSourceStringArraysEqual(currentValues, originalValues);
    const naChanged = this.sourceNotApplicableAttributes.has(attributeId) !== this.sourceOriginalNotApplicableAttributes.has(attributeId);
    return valuesChanged || naChanged;
  }

  toggleSourceNotApplicable(attributeId: number): void {
    if (this.sourceNotApplicableAttributes.has(attributeId)) {
      this.sourceNotApplicableAttributes.delete(attributeId);
      return;
    }

    this.sourceNotApplicableAttributes.add(attributeId);
    if (this.editingAttributeId === attributeId) {
      this.clearSourceEditingState();
    }
  }

  addSourceNewValueBox(attributeId: number): void {
    const attribute = this.getSourceAttribute(attributeId);
    if (!attribute || !attribute.isArray || this.isSourceNotApplicable(attributeId) || this.isSourceValueReadOnly) {
      return;
    }

    const values = [...(this.sourceAttributeValues[attributeId] ?? [])];
    values.push('');
    this.sourceAttributeValues[attributeId] = values;
    this.startEditingSourceAttribute(attributeId, values.length - 1);
  }

  startEditingSourceAttribute(attributeId: number, arrayIndex: number | null = null): void {
    if (this.isSourceNotApplicable(attributeId) || this.isSourceValueReadOnly) {
      return;
    }

    this.editingAttributeId = attributeId;
    this.editingArrayIndex = arrayIndex;
    this.editingFieldType = this.getSourceFieldType(attributeId);

    const values = this.sourceAttributeValues[attributeId] ?? [];
    if (arrayIndex === null) {
      this.editingTruthValueText = values[0] ?? '';
      return;
    }

    this.editingTruthValueText = values[arrayIndex] ?? '';
  }

  onSourceTextFieldInput(event: Event, attributeId: number): void {
    if (this.editingAttributeId !== attributeId) {
      return;
    }

    this.editingTruthValueText = (event.target as HTMLTextAreaElement).value;
  }

  onSourceTruthValueBlur(attributeId: number): void {
    if (this.editingAttributeId !== attributeId) {
      return;
    }

    this.commitSourceEditValue(attributeId);
    this.clearSourceEditingState();
  }

  onSourceSelectFieldChange(event: Event, attributeId: number): void {
    if (this.editingAttributeId !== attributeId) {
      this.startEditingSourceAttribute(attributeId, this.editingArrayIndex);
    }

    this.editingTruthValueText = (event.target as HTMLSelectElement).value;
    this.commitSourceEditValue(attributeId);
  }

  onSourceSelectBlur(event: Event, attributeId: number): void {
    if (this.editingAttributeId !== attributeId) {
      return;
    }

    this.editingTruthValueText = (event.target as HTMLSelectElement).value;
    this.commitSourceEditValue(attributeId);
    this.clearSourceEditingState();
  }

  formatSourceDateForInput(event: Event, attributeId: number): void {
    if (this.editingAttributeId !== attributeId) {
      return;
    }

    const input = event.target as HTMLInputElement;
    const digitsOnly = input.value.replace(/\D/g, '').slice(0, 8);

    let formatted = digitsOnly;
    if (digitsOnly.length > 4) {
      formatted = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}/${digitsOnly.slice(4)}`;
    } else if (digitsOnly.length > 2) {
      formatted = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2)}`;
    }

    this.editingTruthValueText = formatted;
    input.value = formatted;
  }

  onSourceDateKeydown(event: KeyboardEvent): void {
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];

    if (allowedKeys.includes(event.key)) {
      return;
    }

    if (!/^[0-9/]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onSourceDateInputBlur(attributeId: number): void {
    if (this.editingAttributeId !== attributeId) {
      return;
    }

    this.editingTruthValueText = this.normalizeSourceDateDisplay(this.editingTruthValueText);
    this.commitSourceEditValue(attributeId);
    this.clearSourceEditingState();
  }

  onSourceDatePickerChange(event: Event, attributeId: number): void {
    if (this.editingAttributeId !== attributeId) {
      return;
    }

    const raw = (event.target as HTMLInputElement).value;
    this.editingTruthValueText = this.convertSourceDatePickerToDisplay(raw);
    this.commitSourceEditValue(attributeId);
    this.clearSourceEditingState();
  }

  onSourceDatePickerIconMousedown(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  openSourceDatePicker(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    const container = (event.target as HTMLElement).closest('.source-date-input-wrapper');
    const datePickerInput = container?.querySelector('.source-date-picker-hidden') as HTMLInputElement | null;
    if (datePickerInput) {
      datePickerInput.showPicker?.();
      datePickerInput.click();
    }
  }

  getSourceDatePickerValue(): string {
    const value = this.editingTruthValueText.trim();
    const parts = value.split('/');
    if (parts.length !== 3) {
      return '';
    }

    const [month, day, year] = parts;
    if (month.length !== 2 || day.length !== 2 || year.length !== 4) {
      return '';
    }

    return `${year}-${month}-${day}`;
  }

  getSourcePlaceholderText(attributeId: number): string {
    const fieldType = this.getSourceFieldType(attributeId);
    if (fieldType === 'date') {
      return 'MM/DD/YYYY';
    }

    if (fieldType === 'select') {
      return '-- Select --';
    }

    return 'Type text value here...';
  }

  undoSourceChange(attributeId: number, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.sourceAttributeValues[attributeId] = [...(this.sourceOriginalAttributeValues[attributeId] ?? [''])];

    if (this.sourceOriginalNotApplicableAttributes.has(attributeId)) {
      this.sourceNotApplicableAttributes.add(attributeId);
    } else {
      this.sourceNotApplicableAttributes.delete(attributeId);
    }

    if (this.editingAttributeId === attributeId) {
      this.clearSourceEditingState();
    }
  }

  /**
   * The scroll container for the preview. This is the <cdk-virtual-scroll-viewport>
   * element itself. (Previously queried `.ng2-pdf-viewer-container`, which no
   * longer existed — so bbox centring and the zoom-anchor restore that depend on
   * this had silently become no-ops.)
   */
  private getPdfViewerScrollContainer(): HTMLElement | null {
    return this.previewScroller?.nativeElement ?? null;
  }

  private setupPreviewResizeObserver(): void {
    this.previewResizeObserver?.disconnect();
    this.previewResizeObserver = null;

    const container = this.previewScroller?.nativeElement;
    if (!container || typeof ResizeObserver === 'undefined') {
      return;
    }

    // Only relayout when clientWidth actually changes — the observer also fires
    // for internal mutations (canvas paints, spinner swaps). `scrollbar-gutter:
    // stable` on the viewport keeps clientWidth from flipping by the scrollbar
    // width when content appears, so this comparison is meaningful.
    let lastWidth = container.clientWidth;

    this.previewResizeObserver = new ResizeObserver(() => {
      if (!this.sourcePdfDoc) { return; }
      const nowWidth = container.clientWidth;
      if (Math.abs(nowWidth - lastWidth) < 2) { return; }
      lastWidth = nowWidth;

      if (this.previewResizeTimeout !== null) { clearTimeout(this.previewResizeTimeout); }
      this.previewResizeTimeout = setTimeout(() => {
        this.previewResizeTimeout = null;
        this.relayoutPages();
      }, 150);
    });
    this.previewResizeObserver.observe(container);
  }

  onPreviewPdfError(_error: unknown): void {
    this.isPreviewPdfLoading = false;
    this.previewPdfLoadError = 'Unable to load PDF preview for the selected file.';
    this.removePreviewPdfFromSessionByCurrentSelection();
    this.revokePreviewPdfObjectUrl();
    this.pdfSrc = '';
    this.cdr.detectChanges();
  }

  private setCurrentPage(page: number, shouldScroll: boolean): void {
    const clampedPage = Math.min(Math.max(1, this.totalPages), Math.max(1, page));
    this.currentPage = clampedPage;
    this.pageInput = clampedPage;

    if (shouldScroll) {
      this.scrollToPage(clampedPage, true);
    }
  }

  private scrollToPage(page: number, _smooth: boolean): void {
    const viewport = this.pdfViewport;
    if (!viewport || !this.pageNumbers.length) {
      return;
    }
    const target = Math.min(Math.max(1, page), this.pageNumbers.length);

    // The strategy resolves the exact offset from its table and lands there
    // instantly, then emits the landed index — which updateVisiblePage's
    // same-page guard swallows. No suppression window is needed any more.
    viewport.scrollToIndex(target - 1, 'auto');
    this.ignoreScrollIndexUntilProgrammaticScroll = false;

    this.currentPage = target;
    this.pageInput = target;
    this.cdr.markForCheck();

    // Mapped-doc handoff + annotation-chip scroll for the landed page.
    this.syncSelectedFileToVisiblePage(target);
    requestAnimationFrame(() => this.scrollActiveAnnotationIntoView());
  }

  // ────────────────────────────────────────────────────────────────────────
  // Custom PDF.js pipeline (byte-range loading) + CDK virtual-scroll layout
  // ────────────────────────────────────────────────────────────────────────

  /**
   * Load the source PDF via the tune signed-URL endpoint. Uses
   * BlobRangeTransport so pdf.js pulls only the byte ranges it needs, plus a
   * parallel low-priority full download that makes future range requests hit
   * the browser HTTP cache.
   */
  private async loadSourcePdf(): Promise<void> {
    const requestId = this.sourceRequestId.trim();
    if (!requestId) { return; }

    this.isPreviewPdfLoading = true;
    this.previewPdfLoadError = '';
    this.pdfLoadProgress = 0;
    this.cdr.markForCheck();

    try {
      const meta = await firstValueFrom(this.truthManagementService.getTuneSourcePdfUrl(requestId));
      if (!meta?.url) { throw new Error('PDF URL missing'); }

      // Cache-first: if this request's PDF was fully downloaded on an earlier
      // visit (link not expired, same file length), every byte range is served
      // from the stored blob — nothing is downloaded again.
      const cached = meta.length
        ? await sourcePdfCache.get(requestId, meta.length)
        : null;
      const transport = meta.length
        ? new BlobRangeTransport(meta.length, meta.url, cached?.blob ?? null)
        : null;
      const loadOptions: any = transport ? { range: transport } : { url: meta.url };

      // disableStream + disableAutoFetch keep pdf.js from firing hundreds of
      // uncacheable speculative range requests. warmPdfCache below does the
      // single full download (and persists it).
      this.sourcePdfDoc = await (pdfjsLib as any).getDocument({
        ...loadOptions,
        rangeChunkSize: 262144,
        disableStream: true,
        disableAutoFetch: true,
      }).promise;

      this.totalPages = this.sourcePdfDoc.numPages;
      this.pageInput = this.currentPage || 1;
      this.isPreviewPdfLoading = false;
      this.cdr.markForCheck();

      if (meta.pageSizes?.length === this.totalPages) {
        this.sourcePageSizesFromServer = meta.pageSizes.map(([w, h]) => ({ width: w, height: h }));
      }

      await this.loadPageSizes();
      this.computePageLayout();

      if (cached) {
        // Already on disk: nothing to download, progress bar stays hidden.
        this.setPdfProgress(meta.length, meta.length);
      } else if (transport && meta.length) {
        this.warmPdfCache(meta.url, meta.length, requestId, transport);
      }
    } catch (err) {
      this.isPreviewPdfLoading = false;
      this.previewPdfLoadError = 'Failed to load PDF preview.';
      this.cdr.markForCheck();
    }
  }

  /**
   * Low-priority full download so subsequent range requests are served from
   * the browser's HTTP cache. Must stay at 'low' priority to avoid starving
   * page-1 render fetches.
   */
  private async warmPdfCache(
    url: string,
    total: number,
    requestId: string,
    transport: BlobRangeTransport,
  ): Promise<void> {
    this.warmCacheAbortController?.abort();
    const controller = new AbortController();
    this.warmCacheAbortController = controller;
    let stored = false;
    try {
      const response = await fetch(url, {
        // priority: 'low' is not yet in lib.dom types
        ...( { priority: 'low' } as any ),
        signal: controller.signal,
      });
      if (!response.ok || !response.body) { return; }

      // Persist while we stream: the clone's body goes straight to Cache
      // Storage (disk) while the original drives the progress bar — nothing
      // is buffered in JS. A partial body is never stored (cache.put only
      // resolves on a complete stream) and abort cancels both.
      const expiresAt = sourcePdfCache.expiryFromSignedUrl(url);
      const cloneBody = sourcePdfCache.isAvailable() ? response.clone().body : null;
      const storing: Promise<boolean> = cloneBody
        ? sourcePdfCache.put(requestId, cloneBody, total, expiresAt)
        : Promise.resolve(false);

      const reader = response.body.getReader();
      let fetched = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) { break; }
        fetched += value.length;
        this.setPdfProgress(fetched, total);
      }
      stored = await storing;
    } catch (err) {
      if ((err as any)?.name !== 'AbortError') {
      }
    }
    this.setPdfProgress(total, total);

    // From here on, this session's remaining ranges come from disk as well.
    if (stored && !controller.signal.aborted) {
      const cachedNow = await sourcePdfCache.get(requestId, total);
      if (cachedNow) {
        transport.useLocalBlob(cachedNow.blob);
      }
    }
  }

  private setPdfProgress(fetched: number, total: number): void {
    if (!total) { this.pdfLoadProgress = 0; return; }
    this.pdfLoadProgress = Math.min(100, Math.round((fetched / total) * 100));
    this.cdr.markForCheck();
  }

  /**
   * Build the per-page layout model from the (server-supplied or pdf.js-
   * measured) page sizes at the current container width and zoom. The CDK
   * strategy turns pageHeightsPx + pageGapPx into the authoritative offset
   * table; the template stamps only the pages inside the rendered range.
   */
  private computePageLayout(): void {
    const container = this.previewScroller?.nativeElement;
    if (!container || !this.sourcePdfDoc) { return; }

    // `scrollbar-gutter: stable` on the viewport keeps this identical whether or
    // not content overflows yet — no 17px flip when the scrollbar appears.
    // 32 = the two 1rem side margins around a page.
    const contentWidth = Math.max(1, container.clientWidth - 32);
    this.pxPerRem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    this.pageGapPx = this.pageGapRem * this.pxPerRem;

    const total = this.totalPages;
    const numbers: number[] = new Array(total);
    const widths: number[] = new Array(total);
    const heights: number[] = new Array(total);
    const scales: number[] = new Array(total);

    for (let i = 0; i < total; i++) {
      // Never skip a page — a missing entry would shift every later offset.
      // loadPageSizes guarantees a valid size; the fallback is a last defence.
      const raw = this.sourcePageSizesFromServer[i];
      const size = (raw && raw.width > 0 && raw.height > 0)
        ? raw
        : this.sourcePageSizesFromServer[i - 1] ?? { width: 612, height: 792 };
      const scale = this.scaleFor(size, contentWidth);
      numbers[i] = i + 1;
      scales[i] = scale;
      // Unfloored: exactly the canvas pixel size PdfPageCanvasDirective produces
      // for the same scale, so the wrapper box and the canvas agree 1:1.
      widths[i] = size.width * scale;
      heights[i] = size.height * scale;
    }

    this.pageNumbers = numbers;
    this.pageWidthsPx = widths;
    this.pageHeightsPx = heights;
    this.pageScales = scales;
    // Push synchronously as well (the [pageHeights] binding repeats this on the
    // next change-detection pass) so callers can scrollToIndex immediately.
    this.ignoreScrollIndexUntilProgrammaticScroll = true;
    this.pdfScroll?.strategy.setPageHeights(heights, this.pageGapPx);
    this.cdr.markForCheck();
  }

  /** Template helper — page wrappers are sized in rem. */
  remOf(px: number | undefined): string {
    return `${((px ?? 0) / this.pxPerRem).toFixed(4)}rem`;
  }

  /**
   * Ensure `sourcePageSizesFromServer` has exactly `totalPages` valid entries.
   * A same-length array with null/malformed entries used to slip through and
   * silently drop pages from the layout ("stops at 586 of 624"). Fill every
   * missing/invalid slot individually via pdf.js, keeping the slots the server
   * got right so we don't burn extra getPage calls.
   */
  private async loadPageSizes(): Promise<void> {
    if (!this.sourcePdfDoc) { return; }
    const total = this.totalPages;
    const current = this.sourcePageSizesFromServer;
    const isValid = (s: { width: number; height: number } | undefined | null): boolean =>
      !!s && Number.isFinite(s.width) && Number.isFinite(s.height) && s.width > 0 && s.height > 0;

    if (current.length === total && current.every((s) => isValid(s))) { return; }

    const sizes: { width: number; height: number }[] = new Array(total);
    for (let i = 0; i < total; i++) {
      const existing = current[i];
      if (isValid(existing)) {
        sizes[i] = existing!;
        continue;
      }
      const vp = (await this.sourcePdfDoc.getPage(i + 1)).getViewport({ scale: 1 });
      sizes[i] = { width: vp.width, height: vp.height };
    }
    this.sourcePageSizesFromServer = sizes;
  }

  private scaleFor(size: { width: number; height: number }, containerWidth: number): number {
    // Fit-width by default. `this.zoom` multiplies on top so zoom controls work.
    return (containerWidth / size.width) * this.zoom;
  }

  /** The CDK strategy reports which page sits under the viewport's top edge. */
  onVirtualScrolledIndex(index: number): void {
    if (!this.pageNumbers.length) { return; }
    if (this.ignoreScrollIndexUntilProgrammaticScroll) { return; }
    const clamped = Math.min(Math.max(0, index), this.pageNumbers.length - 1);
    this.updateVisiblePage(clamped + 1);
  }

  /**
   * A page canvas finished painting. Two jobs:
   *  1. Size feedback — server pageSizes are unrotated mediabox values, so a
   *     page with an intrinsic /Rotate renders landscape. If the real canvas
   *     size differs from the layout model, correct the model. The strategy's
   *     setPageHeights is anchor-preserving, so the correction never moves the
   *     page under the user's viewport.
   *  2. Re-inject the bbox overlay if the highlighted attribute lives on this
   *     page — CDK recreates the wrapper when it scrolls back into range.
   *     Without recentring, so it can't fight the user's scroll.
   */
  onPageRendered(event: PdfPageRenderedEvent): void {
    const i = event.pageNum - 1;
    if (i < 0 || i >= this.pageHeightsPx.length) { return; }

    if (Math.abs(event.heightPx - this.pageHeightsPx[i]) > 1
      || Math.abs(event.widthPx - this.pageWidthsPx[i]) > 1) {
      const heights = this.pageHeightsPx.slice();
      const widths = this.pageWidthsPx.slice();
      heights[i] = event.heightPx;
      widths[i] = event.widthPx;
      this.pageHeightsPx = heights;
      this.pageWidthsPx = widths;
      this.pdfScroll?.strategy.setPageHeights(heights, this.pageGapPx);
      this.cdr.markForCheck();
    }

    if (this.highlightedSourceAttributeId === null) { return; }
    const sourceData = this.sourceFieldSourceDataMap.get(this.highlightedSourceAttributeId);
    if (!sourceData || this.toAbsolutePreviewPage(sourceData.pageNumber) !== event.pageNum) { return; }
    const pageElement = this.getRenderedSourcePageElement(event.pageNum);
    if (pageElement) {
      this.renderSourceBoundingBoxOverlays(sourceData, pageElement, false);
    }
  }

  /**
   * Apply a scroll-detected page number to the header + funnel the doc-swap /
   * annotation-chip side-effects through the shared paths. The pageInput write
   * is guarded so it never clobbers a number the user is typing.
   */
  private updateVisiblePage(page: number): void {
    if (page === this.currentPage) { return; }
    this.currentPage = page;

    // pageInput is [(ngModel)]-bound to the Goto <input class="page-input">.
    // Never overwrite it while the user is actively typing into it.
    const active = document.activeElement as HTMLElement | null;
    const typingInGoto =
      !!active && active.tagName === 'INPUT' && active.classList.contains('page-input');
    if (!typingInGoto) { this.pageInput = page; }

    this.cdr.markForCheck();
    // activateDocumentById funnel — same-doc guard inside protects from thrash.
    this.syncSelectedFileToVisiblePage(page);
    requestAnimationFrame(() => this.scrollActiveAnnotationIntoView());
  }

  /**
   * Keep the currently-highlighted annotation chip visible in its list as
   * the user scrolls the PDF or crosses into a different mapped document.
   * No-ops if the chip is already fully visible so it doesn't fight
   * user-initiated scroll of the annotation list itself.
   */
  private scrollActiveAnnotationIntoView(): void {
    const listEl = document.querySelector<HTMLElement>('.annotation-list');
    if (!listEl) { return; }
    const activeChip = listEl.querySelector<HTMLElement>('.annotation-row-inline.active');
    if (!activeChip) { return; }
    const listRect = listEl.getBoundingClientRect();
    const chipRect = activeChip.getBoundingClientRect();
    if (chipRect.top < listRect.top || chipRect.bottom > listRect.bottom) {
      activeChip.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  /**
   * Re-fit every page to the current container width / zoom and keep the
   * current page in view. computePageLayout pushes the new heights into the
   * strategy synchronously, so the scrollToIndex right after is exact; the
   * changed [scale] inputs make each visible PdfPageCanvasDirective re-render.
   */
  private relayoutPages(): void {
    if (!this.sourcePdfDoc) { return; }
    const keep = this.currentPage;
    this.computePageLayout();
    this.scrollToPage(keep, false);
  }

  private toggleAnnotationPanel(page: number): void {
    if (this.expandedAnnotationPages.has(page)) {
      this.expandedAnnotationPages.delete(page);
      return;
    }

    this.expandedAnnotationPages.add(page);
    this.getAnnotationPanelState(page);
  }

  private initializeAnnotationPanelStates(): void {
    this.annotationPages.forEach((page) => {
      this.annotationPanelStateByPage[page] = this.getDefaultAnnotationPanelState(page);
    });
  }

  private getDefaultAnnotationPanelState(page: number): AnnotationPanelState {
    return {
      documentName: `Document ${page}`,
      applyToAll: false,
      applyUp: false,
      applyDown: false,
      applyToAllBackup: null,
      applyUpBackup: null,
      applyDownBackup: null,
      comments: '',
      isDocumentNameEditable: false,
    };
  }

  private captureAnnotationDocumentNameBackup(sourcePage: number, direction: 'all' | 'up' | 'down'): Record<number, string> {
    const backup: Record<number, string> = {};
    const targetPages = this.getAnnotationApplyTargetPages(sourcePage, direction)
      .filter((page) => page !== sourcePage);

    targetPages.forEach((targetPage) => {
      backup[targetPage] = this.getAnnotationPanelState(targetPage).documentName;
    });

    return backup;
  }

  private restoreAnnotationDocumentNameFromBackup(sourcePage: number, direction: 'all' | 'up' | 'down'): void {
    const panelState = this.getAnnotationPanelState(sourcePage);
    const backup = this.getAnnotationApplyBackup(panelState, direction);
    if (!backup) {
      return;
    }

    Object.keys(backup).forEach((pageKey) => {
      const targetPage = Number.parseInt(pageKey, 10);
      if (Number.isNaN(targetPage) || targetPage === sourcePage) {
        return;
      }

      const targetState = this.getAnnotationPanelState(targetPage);
      targetState.documentName = backup[targetPage] ?? '';
    });
  }

  private getAnnotationApplyBackup(
    panelState: AnnotationPanelState,
    direction: 'all' | 'up' | 'down',
  ): Record<number, string> | null {
    if (direction === 'all') {
      return panelState.applyToAllBackup;
    }

    if (direction === 'up') {
      return panelState.applyUpBackup;
    }

    return panelState.applyDownBackup;
  }

  private setAnnotationApplyBackup(
    panelState: AnnotationPanelState,
    direction: 'all' | 'up' | 'down',
    backup: Record<number, string> | null,
  ): void {
    if (direction === 'all') {
      panelState.applyToAllBackup = backup;
      return;
    }

    if (direction === 'up') {
      panelState.applyUpBackup = backup;
      return;
    }

    panelState.applyDownBackup = backup;
  }

  private clearOtherAnnotationApplySelections(
    sourcePage: number,
    panelState: AnnotationPanelState,
    selectedDirection: 'all' | 'up' | 'down',
  ): void {
    const directions: Array<'all' | 'up' | 'down'> = ['all', 'up', 'down'];

    directions.forEach((direction) => {
      if (direction === selectedDirection || !this.isAnnotationApplyChecked(panelState, direction)) {
        return;
      }

      this.restoreAnnotationDocumentNameFromBackup(sourcePage, direction);
      this.setAnnotationApplyBackup(panelState, direction, null);
      this.setAnnotationApplyChecked(panelState, direction, false);
    });
  }

  private isAnnotationApplyChecked(
    panelState: AnnotationPanelState,
    direction: 'all' | 'up' | 'down',
  ): boolean {
    if (direction === 'all') {
      return panelState.applyToAll;
    }

    if (direction === 'up') {
      return panelState.applyUp;
    }

    return panelState.applyDown;
  }

  private setAnnotationApplyChecked(
    panelState: AnnotationPanelState,
    direction: 'all' | 'up' | 'down',
    checked: boolean,
  ): void {
    if (direction === 'all') {
      panelState.applyToAll = checked;
      return;
    }

    if (direction === 'up') {
      panelState.applyUp = checked;
      return;
    }

    panelState.applyDown = checked;
  }

  private applyDocumentNameFromPage(sourcePage: number, direction: 'all' | 'up' | 'down'): void {
    const sourceState = this.getAnnotationPanelState(sourcePage);
    const sourceDocumentName = sourceState.documentName;

    const targetPages = this.getAnnotationApplyTargetPages(sourcePage, direction);
    targetPages.forEach((targetPage) => {
      const targetState = this.getAnnotationPanelState(targetPage);
      targetState.documentName = sourceDocumentName;
    });
  }

  private getAnnotationApplyTargetPages(sourcePage: number, direction: 'all' | 'up' | 'down'): number[] {
    const orderedPages = this.getSortedAnnotationPages();
    if (orderedPages.length === 0) {
      return [];
    }

    if (direction === 'all') {
      return orderedPages;
    }

    if (direction === 'up') {
      return orderedPages.filter((page) => page <= sourcePage);
    }

    return orderedPages.filter((page) => page >= sourcePage);
  }

  private isFirstAnnotationPage(page: number): boolean {
    const orderedPages = this.getSortedAnnotationPages();
    return orderedPages.length > 0 && orderedPages[0] === page;
  }

  private isLastAnnotationPage(page: number): boolean {
    const orderedPages = this.getSortedAnnotationPages();
    return orderedPages.length > 0 && orderedPages[orderedPages.length - 1] === page;
  }

  private getSortedAnnotationPages(): number[] {
    return [...this.annotationPages].sort((left, right) => left - right);
  }

  private syncAnnotationPagesForSelectedFile(): void {
    const selectedFile = this.selectedSourceFile;
    if (!selectedFile) {
      return;
    }

    const isSelectedFileChanged = this.activeAnnotationStateFileId !== selectedFile.id;
    const defaultDocumentName = (
      this.annotationDefaultDocumentNameByFileId.get(selectedFile.id)
      || selectedFile.fileName
      || ''
    ).trim();

    // Per-document working state. On a switch, swap in this document's snapshot
    // (unsaved names, comments and apply flags retained) or start fresh. Rows
    // that came from the snapshot keep their values; only rows without one
    // hydrate from the server / original name below. The wholesale swap also
    // keeps apply flags per document (a fresh document starts with none).
    const restoredSnapshot = isSelectedFileChanged
      ? (this.annotationPanelStateByFileId.get(selectedFile.id) ?? null)
      : null;
    if (isSelectedFileChanged) {
      this.annotationPanelStateByPage = restoredSnapshot
        ? this.cloneAnnotationPanelStates(restoredSnapshot)
        : {};
    }

    const pageCount = this.resolveAnnotationPageCountForFile(selectedFile);
    const nextPages = Array.from({ length: pageCount }, (_, index) => index + 1);

    this.annotationPages = nextPages;

    const validPages = new Set(nextPages);
    this.expandedAnnotationPages.forEach((page) => {
      if (!validPages.has(page)) {
        this.expandedAnnotationPages.delete(page);
      }
    });

    const existingPages = Object.keys(this.annotationPanelStateByPage)
      .map((key) => Number.parseInt(key, 10))
      .filter((page) => !Number.isNaN(page));

    existingPages.forEach((page) => {
      if (!validPages.has(page)) {
        delete this.annotationPanelStateByPage[page];
      }
    });

    nextPages.forEach((page) => {
      const existingState = this.annotationPanelStateByPage[page];
      if (!existingState) {
        this.annotationPanelStateByPage[page] = this.getDefaultAnnotationPanelState(page);
      }

      const panelState = this.annotationPanelStateByPage[page];
      const wasRestored = !!restoredSnapshot && !!restoredSnapshot[page];

      // Saved comments / edits are keyed by ABSOLUTE page (the tree and the
      // server contract); `page` here is the row's local index within this
      // document.
      const absolutePage = this.toAbsolutePreviewPage(page);

      const existingCommentForPage = this.existingCommentsByPage[absolutePage];
      const hasExistingCommentForPage = typeof existingCommentForPage === 'string';
      if (!wasRestored && hasExistingCommentForPage && (isSelectedFileChanged || panelState.comments === '')) {
        panelState.comments = existingCommentForPage;
      }

      const editedNameForPage = this.editedDocNameByPage[absolutePage];
      const hasEditedNameForPage = typeof editedNameForPage === 'string' && editedNameForPage.trim().length > 0;
      if (!defaultDocumentName && !hasEditedNameForPage) {
        return;
      }

      const hasPlaceholderValue = !panelState.documentName.trim() || panelState.documentName === `Document ${page}`;
      if (!wasRestored && (isSelectedFileChanged || hasPlaceholderValue)) {
        panelState.documentName = hasEditedNameForPage ? editedNameForPage : defaultDocumentName;
      }
    });

    if (isSelectedFileChanged) {
      this.closeAnnotationDocumentTypeDropdown();
      nextPages.forEach((page) => {
        delete this.annotationDocumentTypeSearchByPage[page];
      });
      // The open apply menu's page index belongs to the previous document.
      this.closeAnnotationApplyMenu();
    }

    this.activeAnnotationStateFileId = selectedFile.id;

    this.setCurrentPage(this.currentPage, false);

    if (
      this.activeAnnotationDocumentTypeDropdownPage !== null
      && !validPages.has(this.activeAnnotationDocumentTypeDropdownPage)
    ) {
      this.closeAnnotationDocumentTypeDropdown();
    }

    Object.keys(this.annotationDocumentTypeSearchByPage)
      .map((key) => Number.parseInt(key, 10))
      .forEach((page) => {
        if (!Number.isNaN(page) && !validPages.has(page)) {
          delete this.annotationDocumentTypeSearchByPage[page];
        }
      });

    this.captureAnnotationSaveBaselineForSelectedFile(isSelectedFileChanged);
  }

  private captureAnnotationSaveBaselineForSelectedFile(resetForFile: boolean): void {
    const selectedFile = this.selectedSourceFile;
    if (!selectedFile) {
      this.annotationSaveBaselineByPage = {};
      this.annotationSaveBaselineFileId = null;
      return;
    }

    if (resetForFile || this.annotationSaveBaselineFileId !== selectedFile.id) {
      this.annotationSaveBaselineByPage = {};
      this.annotationSaveBaselineFileId = selectedFile.id;
    }

    const validPages = new Set(this.annotationPages);
    Object.keys(this.annotationSaveBaselineByPage)
      .map((key) => Number.parseInt(key, 10))
      .forEach((page) => {
        if (!Number.isNaN(page) && !validPages.has(page)) {
          delete this.annotationSaveBaselineByPage[page];
        }
      });

    this.annotationPages.forEach((page) => {
      if (!this.annotationSaveBaselineByPage[page]) {
        this.annotationSaveBaselineByPage[page] = this.createAnnotationSaveSnapshot(page);
      }
    });
  }

  private commitAnnotationSaveBaselineForSelectedFile(): void {
    const selectedFile = this.selectedSourceFile;
    if (!selectedFile) {
      return;
    }

    this.annotationSaveBaselineFileId = selectedFile.id;
    this.annotationSaveBaselineByPage = {};
    this.annotationPages.forEach((page) => {
      this.annotationSaveBaselineByPage[page] = this.createAnnotationSaveSnapshot(page);
    });
  }

  private createAnnotationSaveSnapshot(page: number): AnnotationSaveSnapshot {
    const state = this.getAnnotationPanelState(page);
    return {
      comments: state.comments,
      documentName: state.documentName,
    };
  }

  /** Deep-copies per-row annotation state so snapshots never alias the live map. */
  private cloneAnnotationPanelStates(
    states: Record<number, AnnotationPanelState>,
  ): Record<number, AnnotationPanelState> {
    const copy: Record<number, AnnotationPanelState> = {};
    Object.entries(states).forEach(([key, state]) => {
      const page = Number(key);
      if (!Number.isFinite(page)) { return; }
      copy[page] = {
        ...state,
        applyToAllBackup: state.applyToAllBackup ? { ...state.applyToAllBackup } : null,
        applyUpBackup: state.applyUpBackup ? { ...state.applyUpBackup } : null,
        applyDownBackup: state.applyDownBackup ? { ...state.applyDownBackup } : null,
      };
    });
    return copy;
  }

  /** Snapshot the selected document's rows so unsaved edits survive switching documents. */
  private persistCurrentAnnotationPanelState(): void {
    if (!this.selectedFileId) { return; }
    this.annotationPanelStateByFileId.set(
      this.selectedFileId,
      this.cloneAnnotationPanelStates(this.annotationPanelStateByPage),
    );
  }

  /** Absolute PDF page for a document's local row index (same math as toAbsolutePreviewPage). */
  private absolutePageFor(file: SourceFileItem, localPage: number): number {
    const start = file.startPageNumber;
    return typeof start === 'number' && Number.isFinite(start) && start > 0 ? start + localPage - 1 : localPage;
  }

  private getOriginalDocumentName(file: SourceFileItem): string {
    return (this.annotationDefaultDocumentNameByFileId.get(file.id) || file.fileName || '').trim();
  }

  /**
   * Diff one row of a document against what the server currently holds.
   * - name: pending when it differs from the effective saved name (server edit
   *   if present, else the document's original mapped name). A `Document N`
   *   placeholder / missing row counts as "whatever is saved".
   * - docNameEdit: emitted whenever the effective name differs from the
   *   ORIGINAL name — so a revert to the original drops the key.
   * - comment: pending when it differs from the saved comment ('' if none);
   *   the saved comment is carried forward when unchanged.
   */
  private diffAnnotationPage(
    file: SourceFileItem,
    states: Record<number, AnnotationPanelState>,
    page: number,
  ): { key: string; docNameEdit: string | null; annotation: string | null; pending: boolean } {
    const absolutePage = this.absolutePageFor(file, page);
    const key = String(absolutePage);
    const state = states[page];
    const originalName = this.getOriginalDocumentName(file);

    const savedEdit = this.editedDocNameByPage[absolutePage];
    const savedName = typeof savedEdit === 'string' && savedEdit.trim() ? savedEdit.trim() : originalName;
    const savedCommentRaw = this.existingCommentsByPage[absolutePage];
    const savedComment = typeof savedCommentRaw === 'string' ? savedCommentRaw : '';

    const rawName = state ? (state.documentName || '') : '';
    const trimmedName = rawName.trim();
    const isPlaceholder = !trimmedName || trimmedName === `Document ${page}`;
    const effectiveName = isPlaceholder ? savedName : trimmedName;

    let pending = effectiveName !== savedName;
    const docNameEdit = effectiveName && effectiveName !== originalName
      ? (isPlaceholder ? savedName : rawName)
      : null;

    const currentComment = state ? (state.comments ?? '') : savedComment;
    let annotation: string | null = null;
    if (currentComment !== savedComment) {
      pending = true;
      annotation = currentComment;
    } else if (typeof savedCommentRaw === 'string') {
      annotation = savedCommentRaw;
    }

    return { key, docNameEdit, annotation, pending };
  }

  private getPendingAnnotationChanges(
    file: SourceFileItem,
    states: Record<number, AnnotationPanelState>,
  ): { docNameEdits: Record<string, string>; annotations: Record<string, string>; pendingPages: Set<number> } {
    const docNameEdits: Record<string, string> = {};
    const annotations: Record<string, string> = {};
    const pendingPages = new Set<number>();
    const pageCount = this.resolveAnnotationPageCountForFile(file);

    for (let page = 1; page <= pageCount; page++) {
      const diff = this.diffAnnotationPage(file, states, page);
      if (diff.pending) { pendingPages.add(page); }
      if (diff.docNameEdit !== null) { docNameEdits[diff.key] = diff.docNameEdit; }
      if (diff.annotation !== null) { annotations[diff.key] = diff.annotation; }
    }
    return { docNameEdits, annotations, pendingPages };
  }

  /** Whole-row highlight: this local page of the selected document has an unsaved change. */
  isPageChangedUnsaved(page: number): boolean {
    const file = this.selectedSourceFile;
    if (!file) { return false; }
    return this.diffAnnotationPage(file, this.annotationPanelStateByPage, page).pending;
  }

  /** True when this row's current document name differs from what's currently persisted
   *  for the same absolute page — i.e., the user has actually picked a new name in this
   *  session. Used to gate Apply to entire document / Apply up / Apply down. */
  hasDocNameChangeForPage(page: number): boolean {
    const file = this.selectedSourceFile;
    if (!file) { return false; }
    const state = this.annotationPanelStateByPage[page];
    if (!state) { return false; }
    const absolutePage = this.absolutePageFor(file, page);
    const originalName = this.getOriginalDocumentName(file);
    const savedEdit = this.editedDocNameByPage[absolutePage];
    const savedName = typeof savedEdit === 'string' && savedEdit.trim() ? savedEdit.trim() : originalName;
    const rawName = state.documentName || '';
    const trimmedName = rawName.trim();
    const isPlaceholder = !trimmedName || trimmedName === `Document ${page}`;
    const effectiveName = isPlaceholder ? savedName : trimmedName;
    return effectiveName !== savedName;
  }

  private buildAnnotationSavePayload(): TuneSaveAnnotationsRequest | null {
    const annotations: Record<string, string> = {};
    const docNameEdits: Record<string, string> = {};

    // Every document with retained (unsaved) row state, plus the live one —
    // edits made in a document the user has since scrolled away from must
    // still be saved.
    const statesByFile = new Map<string, Record<number, AnnotationPanelState>>(this.annotationPanelStateByFileId);
    if (this.selectedFileId) {
      statesByFile.set(this.selectedFileId, this.annotationPanelStateByPage);
    }

    const pendingRanges: Array<{ start: number; end: number }> = [];
    const pendingEdits: Record<string, string> = {};
    const pendingComments: Record<string, string> = {};
    let hasPendingChange = false;

    statesByFile.forEach((states, fileId) => {
      const file = this.sourceFiles.find((f) => f.id === fileId);
      if (!file) { return; }
      const diff = this.getPendingAnnotationChanges(file, states);
      if (diff.pendingPages.size === 0) { return; }
      hasPendingChange = true;
      // Only a document with a real page range can displace server keys.
      if (typeof file.startPageNumber === 'number' && Number.isFinite(file.startPageNumber)) {
        pendingRanges.push({
          start: this.absolutePageFor(file, 1),
          end: this.absolutePageFor(file, this.resolveAnnotationPageCountForFile(file)),
        });
      }
      Object.assign(pendingEdits, diff.docNameEdits);
      Object.assign(pendingComments, diff.annotations);
    });

    const hasStatusChange = this.sourceReviewStatus !== this.sourceReviewStatusBaseline;
    if (!hasPendingChange && !hasStatusChange) {
      return null;
    }

    const inPendingRange = (pageKey: string): boolean => {
      const absolute = Number(pageKey);
      return Number.isFinite(absolute) && pendingRanges.some((r) => absolute >= r.start && absolute <= r.end);
    };

    // Keys are ABSOLUTE pages (server contract; what the tree reads). Documents
    // with pending changes are recomputed from their row state; every other
    // document's saved keys are preserved verbatim.
    Object.entries(this.editedDocNameByPage).forEach(([pageKey, name]) => {
      if (typeof name === 'string' && name.trim() && !inPendingRange(pageKey)) {
        docNameEdits[pageKey] = name;
      }
    });
    Object.entries(this.existingCommentsByPage).forEach(([pageKey, comment]) => {
      if (typeof comment === 'string' && !inPendingRange(pageKey)) {
        annotations[pageKey] = comment;
      }
    });
    Object.assign(docNameEdits, pendingEdits);
    Object.assign(annotations, pendingComments);

    const payload: TuneSaveAnnotationsRequest = {
      annotations,
      docNameEdits,
      hilStatus: this.mapReviewStatusToHilStatus(this.sourceReviewStatus),
    };

    return payload;
  }

  private mapReviewStatusToHilStatus(status: SourceReviewStatus): TuneHilStatus {
    if (status === 'completed') {
      return 'COMPLETED';
    }

    if (status === 'failed') {
      return 'FAILED';
    }

    return 'NEEDED';
  }

  private mapHilStatusToReviewStatus(status: TuneHilStatus | string | undefined | null): SourceReviewStatus {
    if (status === 'COMPLETED') {
      return 'completed';
    }

    if (status === 'FAILED') {
      return 'failed';
    }

    return 'under-review';
  }

  /** `page` is the row's local index; saved edits are keyed by absolute page. */
  isPageEdited(page: number): boolean {
    return Object.prototype.hasOwnProperty.call(this.editedDocNameByPage, this.toAbsolutePreviewPage(page));
  }

  isFileEdited(file: SourceFileItem): boolean {
    return this.getEditedPagesForFile(file).length > 0;
  }

  getEditedNamesForFile(file: SourceFileItem): string[] {
    const pages = this.getEditedPagesForFile(file);
    const names: string[] = [];
    const seen = new Set<string>();
    pages.forEach((page) => {
      const name = this.editedDocNameByPage[page];
      if (typeof name === 'string' && name.trim() && !seen.has(name)) {
        seen.add(name);
        names.push(name);
      }
    });
    return names;
  }

  getEditedNameEntriesForFile(file: SourceFileItem): { page: number; name: string }[] {
    const pages = this.getEditedPagesForFile(file);
    return pages
      .map((page) => ({ page, name: this.editedDocNameByPage[page] }))
      .filter((entry) => typeof entry.name === 'string' && entry.name.trim().length > 0)
      .sort((a, b) => a.page - b.page);
  }

  private getEditedPagesForFile(file: SourceFileItem): number[] {
    const start = typeof file.startPageNumber === 'number' && Number.isFinite(file.startPageNumber)
      ? file.startPageNumber
      : null;
    const end = typeof file.endPageNumber === 'number' && Number.isFinite(file.endPageNumber)
      ? file.endPageNumber
      : null;
    if (start === null || end === null) {
      return [];
    }
    return Object.keys(this.editedDocNameByPage)
      .map((k) => Number(k))
      .filter((p) => Number.isFinite(p) && p >= start && p <= end)
      .sort((a, b) => a - b);
  }

  private resolveAnnotationPageCountForFile(file: SourceFileItem): number {
    if (typeof file.totalDocumentPages === 'number' && Number.isFinite(file.totalDocumentPages) && file.totalDocumentPages > 0) {
      return Math.floor(file.totalDocumentPages);
    }

    if (
      typeof file.startPageNumber === 'number'
      && Number.isFinite(file.startPageNumber)
      && typeof file.endPageNumber === 'number'
      && Number.isFinite(file.endPageNumber)
      && file.endPageNumber >= file.startPageNumber
    ) {
      return Math.floor((file.endPageNumber - file.startPageNumber) + 1);
    }

    if (typeof file.endPageNumber === 'number' && Number.isFinite(file.endPageNumber) && file.endPageNumber > 0) {
      return Math.floor(file.endPageNumber);
    }

    return 1;
  }

  private isMiddleAnnotationPage(page: number): boolean {
    const orderedPages = this.getSortedAnnotationPages();
    if (orderedPages.length <= 2) {
      return false;
    }

    return page > orderedPages[0] && page < orderedPages[orderedPages.length - 1];
  }

  private setupPreviewWheelZoom(): void {
    const container = this.previewScroller?.nativeElement;
    if (!container) {
      return;
    }

    container.removeEventListener('wheel', this.boundPreviewWheelHandler);
    container.addEventListener('wheel', this.boundPreviewWheelHandler, { passive: false });
  }

  private removePreviewWheelZoom(): void {
    this.previewScroller?.nativeElement.removeEventListener('wheel', this.boundPreviewWheelHandler);
  }

  private getAnnotationDocumentTypeMatchScore(query: string, value: string): number {
    const directIndex = value.indexOf(query);
    if (directIndex >= 0) {
      return directIndex;
    }

    let queryIndex = 0;
    let gapPenalty = 0;
    let lastMatchIndex = -1;

    for (let valueIndex = 0; valueIndex < value.length && queryIndex < query.length; valueIndex += 1) {
      if (value[valueIndex] !== query[queryIndex]) {
        continue;
      }

      if (lastMatchIndex >= 0) {
        gapPenalty += Math.max(0, valueIndex - lastMatchIndex - 1);
      } else {
        gapPenalty += valueIndex;
      }

      lastMatchIndex = valueIndex;
      queryIndex += 1;
    }

    if (queryIndex !== query.length) {
      return -1;
    }

    return value.length + gapPenalty;
  }

  private loadAnnotationDocumentTypes(options: { force?: boolean } = {}): void {
    if (!options.force) {
      const cached = this.readAnnotationDocumentTypesFromSession();
      if (cached.length > 0) {
        this.annotationDocumentTypeOptions = cached;
        return;
      }
    }

    this.sequencingDocumentTypesSub?.unsubscribe();
    this.sequencingDocumentTypesSub = this.truthManagementService.getSequencingDocumentTypes({
      module: this.annotationDocumentTypesModule,
    }).subscribe({
      next: (response) => {
        const normalizedTypes = this.normalizeAnnotationDocumentTypes(response);
        this.annotationDocumentTypeOptions = normalizedTypes;
        this.writeAnnotationDocumentTypesToSession(normalizedTypes);
      },
      error: (err) => {
        this.annotationDocumentTypeOptions = [];
      },
    });
  }

  private normalizeAnnotationDocumentTypes(raw: unknown): string[] {
    const items = this.unwrapDocumentTypesPayload(raw);
    if (items.length === 0) {
      return [];
    }

    const seen = new Set<string>();
    const normalized: string[] = [];

    items.forEach((item) => {
      const documentType = this.pickDocumentTypeName(item);
      if (!documentType || seen.has(documentType)) {
        return;
      }

      seen.add(documentType);
      normalized.push(documentType);
    });

    return normalized;
  }

  private unwrapDocumentTypesPayload(raw: unknown): unknown[] {
    if (Array.isArray(raw)) {
      return raw;
    }
    if (raw && typeof raw === 'object') {
      const obj = raw as Record<string, unknown>;
      const wrapperKeys = [
        'document_types',
        'documentTypes',
        'document_models',
        'documentModels',
        'documents',
        'data',
        'results',
        'items',
      ];
      for (const key of wrapperKeys) {
        if (Array.isArray(obj[key])) {
          return obj[key] as unknown[];
        }
      }
    }
    return [];
  }

  private pickDocumentTypeName(item: unknown): string {
    if (typeof item === 'string') {
      return item.trim();
    }
    if (item && typeof item === 'object') {
      const obj = item as Record<string, unknown>;
      const candidateKeys = ['document_type', 'documentType', 'document_name', 'documentName', 'name', 'value'];
      for (const key of candidateKeys) {
        const value = obj[key];
        if (typeof value === 'string' && value.trim().length > 0) {
          return value.trim();
        }
      }
    }
    return '';
  }

  private readAnnotationDocumentTypesFromSession(): string[] {
    const raw = sessionStorage.getItem(SOURCE_ANNOTATION_DOCUMENT_TYPES_STORAGE_KEY);
    if (!raw) {
      return [];
    }

    try {
      const parsed = JSON.parse(raw) as Partial<AnnotationDocumentTypesCacheState>;
      if (parsed.module !== this.annotationDocumentTypesModule || !Array.isArray(parsed.documentTypes)) {
        return [];
      }

      return parsed.documentTypes
        .filter((value): value is string => typeof value === 'string')
        .map((value) => value.trim())
        .filter((value) => value.length > 0);
    } catch {
      return [];
    }
  }

  private writeAnnotationDocumentTypesToSession(documentTypes: string[]): void {
    const payload: AnnotationDocumentTypesCacheState = {
      module: this.annotationDocumentTypesModule,
      documentTypes,
    };

    try {
      sessionStorage.setItem(SOURCE_ANNOTATION_DOCUMENT_TYPES_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // Ignore storage write failures and keep in-memory dropdown options.
    }
  }

  private initializeSourceAttributesState(): void {
    this.sourceAttributeValues = {};
    this.sourceOriginalAttributeValues = {};

    this.sourceAttributes.forEach((attribute) => {
      const normalizedValues = attribute.initialValues.length > 0 ? [...attribute.initialValues] : [''];
      this.sourceAttributeValues[attribute.id] = [...normalizedValues];
      this.sourceOriginalAttributeValues[attribute.id] = [...normalizedValues];
    });

    this.sourceNotApplicableAttributes.clear();
    this.sourceOriginalNotApplicableAttributes.clear();
  }

  private cloneSourceValueRecord(record: Record<number, string[]>): Record<number, string[]> {
    const clone: Record<number, string[]> = {};
    Object.keys(record).forEach((key) => {
      const numericKey = Number.parseInt(key, 10);
      if (Number.isNaN(numericKey)) {
        return;
      }
      clone[numericKey] = [...(record[numericKey] ?? [])];
    });
    return clone;
  }

  private cloneSourceAttributes(attributes: SourceAttributeItem[]): SourceAttributeItem[] {
    return attributes.map((attribute) => ({
      ...attribute,
      initialValues: [...attribute.initialValues],
      allowedValues: attribute.allowedValues ? [...attribute.allowedValues] : undefined,
    }));
  }

  private persistCurrentFileAttributeState(): void {
    if (!this.selectedFileId) {
      return;
    }

    const existingState = this.sourceFileAttributeStateByFileId.get(this.selectedFileId);
    if (!existingState) {
      return;
    }

    existingState.attributes = this.cloneSourceAttributes(this.sourceAttributes);
    existingState.values = this.cloneSourceValueRecord(this.sourceAttributeValues);
    existingState.originalValues = this.cloneSourceValueRecord(this.sourceOriginalAttributeValues);
    existingState.notApplicable = new Set(this.sourceNotApplicableAttributes);
    existingState.originalNotApplicable = new Set(this.sourceOriginalNotApplicableAttributes);
  }

  private restoreAttributeStateForFile(fileId: string): void {
    const state = this.sourceFileAttributeStateByFileId.get(fileId);
    if (!state) {
      this.sourceAttributes = [];
      this.initializeSourceAttributesState();
      this.sourceFieldSourceDataMap = new Map();
      this.clearSourceEditingState();
      return;
    }

    this.sourceAttributes = this.cloneSourceAttributes(state.attributes);
    this.sourceAttributeValues = this.cloneSourceValueRecord(state.values);
    this.sourceOriginalAttributeValues = this.cloneSourceValueRecord(state.originalValues);
    this.sourceNotApplicableAttributes = new Set(state.notApplicable);
    this.sourceOriginalNotApplicableAttributes = new Set(state.originalNotApplicable);
    this.sourceFieldSourceDataMap = new Map(this.sourceFieldSourceDataByFileId.get(fileId) || []);
    this.clearSourceEditingState();
  }

  isSourceAttributeHighlighted(attributeId: number): boolean {
    return this.highlightedSourceAttributeId === attributeId;
  }

  onSourceAttributeFieldClick(attributeId: number, event?: MouseEvent): void {
    if (event && this.shouldIgnoreSourceAttributeRowClick(event)) {
      return;
    }

    const sourceData = this.sourceFieldSourceDataMap.get(attributeId);
    if (this.highlightedSourceAttributeId === attributeId && sourceData?.boundingBoxes?.length) {
      this.scrollToSourceBoundingBox();
      return;
    }

    this.clearSourceBoundingBoxHighlight();

    if (!sourceData?.pageNumber) {
      this.setCurrentPage(this.toAbsolutePreviewPage(1), true);
      return;
    }

    this.setCurrentPage(this.toAbsolutePreviewPage(sourceData.pageNumber), true);

    if (!sourceData.boundingBoxes.length) {
      return;
    }

    this.highlightedSourceAttributeId = attributeId;
    this.cdr.detectChanges();
    this.renderSourceBoundingBoxOverlaysWithRetry(sourceData, 0);
  }

  private shouldIgnoreSourceAttributeRowClick(event: MouseEvent): boolean {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return false;
    }

    return !!target.closest('button, input, textarea, select, label, a, .source-date-picker-icon');
  }

  private getSourceAttribute(attributeId: number): SourceAttributeItem | undefined {
    return this.sourceAttributes.find((attribute) => attribute.id === attributeId);
  }

  private commitSourceEditValue(attributeId: number): void {
    const attribute = this.getSourceAttribute(attributeId);
    if (!attribute) {
      return;
    }

    const currentValues = [...(this.sourceAttributeValues[attributeId] ?? [''])];
    if (this.editingArrayIndex === null || !attribute.isArray) {
      currentValues[0] = this.editingTruthValueText.trim();
      this.sourceAttributeValues[attributeId] = [currentValues[0]];
      return;
    }

    while (currentValues.length <= this.editingArrayIndex) {
      currentValues.push('');
    }

    currentValues[this.editingArrayIndex] = this.editingTruthValueText.trim();
    this.sourceAttributeValues[attributeId] = currentValues;
  }

  private clearSourceEditingState(): void {
    this.editingAttributeId = null;
    this.editingArrayIndex = null;
    this.editingFieldType = 'text';
    this.editingTruthValueText = '';
  }

  private areSourceStringArraysEqual(left: string[], right: string[]): boolean {
    if (left.length !== right.length) {
      return false;
    }

    for (let index = 0; index < left.length; index += 1) {
      if ((left[index] ?? '').trim() !== (right[index] ?? '').trim()) {
        return false;
      }
    }

    return true;
  }

  private normalizeSourceDateDisplay(value: string): string {
    const raw = value.trim();
    const digits = raw.replace(/\D/g, '');
    if (digits.length !== 8) {
      return raw;
    }

    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  }

  private convertSourceDatePickerToDisplay(value: string): string {
    const parts = value.split('-');
    if (parts.length !== 3) {
      return value;
    }

    const [year, month, day] = parts;
    if (!year || !month || !day) {
      return value;
    }

    return `${month}/${day}/${year}`;
  }

  private isSourceValueInvalid(attributeId: number): boolean {
    const attribute = this.getSourceAttribute(attributeId);
    if (!attribute) {
      return false;
    }

    const nonEmptyValues = this.getSourceDisplayValueAsArray(attributeId)
      .map((value) => value.trim())
      .filter((value) => value.length > 0);

    if (nonEmptyValues.length === 0) {
      return false;
    }

    if (attribute.fieldType === 'select') {
      const allowedValues = new Set((attribute.allowedValues ?? []).map((option) => option.value));
      return nonEmptyValues.some((value) => !allowedValues.has(value));
    }

    if (attribute.fieldType === 'date') {
      return nonEmptyValues.some((value) => !this.isSourceDateDisplayValid(value));
    }

    return false;
  }

  private isSourceDateDisplayValid(value: string): boolean {
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
    if (!match) {
      return false;
    }

    const month = Number.parseInt(match[1], 10);
    const day = Number.parseInt(match[2], 10);
    const year = Number.parseInt(match[3], 10);

    if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) {
      return false;
    }

    const parsedDate = new Date(year, month - 1, day);
    return parsedDate.getFullYear() === year && parsedDate.getMonth() === month - 1 && parsedDate.getDate() === day;
  }

  private resolveSelectedLoanContext(): SourceLoanContext | null {
    const routeState = this.router.getCurrentNavigation()?.extras?.state?.['selectedLoanContext'] as SourceLoanContext | undefined;
    const historyState = history.state?.selectedLoanContext as SourceLoanContext | undefined;

    const contextFromNavigation = this.isValidLoanContext(routeState)
      ? routeState
      : (this.isValidLoanContext(historyState) ? historyState : null);

    if (contextFromNavigation) {
      this.persistLoanContext(contextFromNavigation);
      return contextFromNavigation;
    }

    try {
      const serialized = sessionStorage.getItem(SOURCE_LOAN_CONTEXT_STORAGE_KEY);
      if (!serialized) {
        return null;
      }

      const parsed = JSON.parse(serialized) as SourceLoanContext;
      return this.isValidLoanContext(parsed) ? parsed : null;
    } catch {
      // Ignore storage parse errors and continue with query-param fallback.
    }

    const loanNumberFromQuery = this.route.snapshot.queryParamMap.get('loanNumber');
    const requestIdFromQuery = this.route.snapshot.queryParamMap.get('requestId');
    if (!loanNumberFromQuery || !loanNumberFromQuery.trim()) {
      return null;
    }

    const trimmedLoanNumber = loanNumberFromQuery.trim();
    const fallbackContext: SourceLoanContext = {
      loanNumber: trimmedLoanNumber,
      row: {
        loanNumber: trimmedLoanNumber,
        documentCount: 0,
        lastModifiedOn: '',
        lastModifiedBy: '',
        status: 'Active',
        actions: 'icons',
        requestId: requestIdFromQuery?.trim() || undefined,
      },
      requestId: requestIdFromQuery?.trim() || undefined,
    };

    this.persistLoanContext(fallbackContext);
    return fallbackContext;
  }

  private isValidLoanContext(context: SourceLoanContext | null | undefined): context is SourceLoanContext {
    return !!context && typeof context.loanNumber === 'string' && context.loanNumber.trim().length > 0;
  }

  private persistLoanContext(context: SourceLoanContext): void {
    try {
      sessionStorage.setItem(SOURCE_LOAN_CONTEXT_STORAGE_KEY, JSON.stringify(context));
    } catch {
      // Ignore storage write failures and keep route-state behavior.
    }
  }

  private resolveSourceRequestId(context: SourceLoanContext): string {
    const contextLevelRequestId = (context.requestId || '').trim();
    if (contextLevelRequestId) {
      return contextLevelRequestId;
    }

    const rowRequestId = (context.row.requestId || '').trim();
    if (rowRequestId) {
      return rowRequestId;
    }

    const requestIdFromQuery = (this.route.snapshot.queryParamMap.get('requestId') || '').trim();
    return requestIdFromQuery;
  }

  private getSourceClientRequestId(): string {
    return String(this.selectedLoanContext?.row?.clientId || '').trim();
  }

  private loadPreviewPdfForSelectedFile(): void {
    const selectedFile = this.selectedSourceFile;
    const requestId = this.sourceRequestId.trim();
    const clientRequestId = this.getSourceClientRequestId();

    this.sequencingExecutionDataSub?.unsubscribe();
    this.previewPdfRequestEpoch += 1;

    if (!selectedFile) {
      this.clearPreviewPdfState();
      return;
    }

    if (!requestId || !clientRequestId) {
      this.isPreviewPdfLoading = false;
      this.previewPdfLoadError = 'Missing request context for PDF preview.';
      this.revokePreviewPdfObjectUrl();
      this.pdfSrc = '';
      return;
    }

    const previewCacheKey = this.buildPreviewPdfCacheKey(requestId, clientRequestId, selectedFile.id);
    const cachedPreviewPdf = this.readPreviewPdfFromSession(previewCacheKey);
    if (cachedPreviewPdf) {
      this.isPreviewPdfLoading = true;
      this.previewPdfLoadError = '';
      this.revokePreviewPdfObjectUrl();
      this.pdfSrc = cachedPreviewPdf;
      this.cdr.detectChanges();
      return;
    }

    this.isPreviewPdfLoading = true;
    this.previewPdfLoadError = '';
    this.revokePreviewPdfObjectUrl();
    this.pdfSrc = '';

    const requestEpoch = this.previewPdfRequestEpoch;
    this.sequencingExecutionDataSub = this.truthManagementService.getSequencingExecutionData({
      request_id: requestId,
      client_request_id: clientRequestId,
      data_type: 'pdfs',
    }).subscribe({
      next: (blob) => {
        this.ngZone.run(() => {
          if (requestEpoch !== this.previewPdfRequestEpoch) {
            return;
          }

          if (!(blob instanceof Blob) || blob.size === 0) {
            this.isPreviewPdfLoading = false;
            this.previewPdfLoadError = 'PDF preview is unavailable for this file.';
            this.pdfSrc = '';
            this.cdr.detectChanges();
            return;
          }

          this.previewPdfBlobUrl = URL.createObjectURL(blob);
          this.pdfSrc = this.previewPdfBlobUrl;
          this.writePreviewPdfToSession(previewCacheKey, blob);
          this.isPreviewPdfLoading = true;
          this.previewPdfLoadError = '';
          this.cdr.detectChanges();
        });
      },
      error: () => {
        this.ngZone.run(() => {
          if (requestEpoch !== this.previewPdfRequestEpoch) {
            return;
          }

          this.isPreviewPdfLoading = false;
          this.previewPdfLoadError = 'Unable to load PDF preview for the selected file.';
          this.removePreviewPdfFromSession(previewCacheKey);
          this.revokePreviewPdfObjectUrl();
          this.pdfSrc = '';
          this.cdr.detectChanges();
        });
      },
    });
  }

  private clearPreviewPdfState(): void {
    this.sequencingExecutionDataSub?.unsubscribe();
    this.previewPdfRequestEpoch += 1;
    this.clearSourceBoundingBoxHighlight();
    this.isPreviewPdfLoading = false;
    this.previewPdfLoadError = '';
    this.revokePreviewPdfObjectUrl();
    this.pdfSrc = '';
  }

  private revokePreviewPdfObjectUrl(): void {
    if (this.previewPdfBlobUrl) {
      URL.revokeObjectURL(this.previewPdfBlobUrl);
      this.previewPdfBlobUrl = '';
    }
  }

  private buildPreviewPdfCacheKey(requestId: string, clientRequestId: string, fileId: string): string {
    return `${SOURCE_PREVIEW_PDF_CACHE_PREFIX}${requestId}::${clientRequestId}::${fileId}`;
  }

  private readPreviewPdfFromSession(cacheKey: string): string {
    try {
      const value = sessionStorage.getItem(cacheKey) || '';
      return value.startsWith('data:application/pdf') ? value : '';
    } catch {
      return '';
    }
  }

  private writePreviewPdfToSession(cacheKey: string, blob: Blob): void {
    const reader = new FileReader();

    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      if (!result.startsWith('data:application/pdf')) {
        return;
      }

      try {
        sessionStorage.setItem(cacheKey, result);
      } catch {
        // Ignore storage failures and keep in-memory preview behavior.
      }
    };

    reader.onerror = () => {
      // Ignore conversion failures and keep in-memory preview behavior.
    };

    reader.readAsDataURL(blob);
  }

  private removePreviewPdfFromSession(cacheKey: string): void {
    try {
      sessionStorage.removeItem(cacheKey);
    } catch {
      // Ignore storage failures.
    }
  }

  private clearPreviewPdfSessionCache(): void {
    try {
      const keysToRemove: string[] = [];
      for (let index = 0; index < sessionStorage.length; index += 1) {
        const key = sessionStorage.key(index);
        if (key && key.startsWith(SOURCE_PREVIEW_PDF_CACHE_PREFIX)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => {
        sessionStorage.removeItem(key);
      });
    } catch {
      // Ignore storage failures.
    }
  }

  private removePreviewPdfFromSessionByCurrentSelection(): void {
    const selectedFile = this.selectedSourceFile;
    const requestId = this.sourceRequestId.trim();
    const clientRequestId = this.getSourceClientRequestId();
    if (!selectedFile || !requestId || !clientRequestId) {
      return;
    }

    const previewCacheKey = this.buildPreviewPdfCacheKey(requestId, clientRequestId, selectedFile.id);
    this.removePreviewPdfFromSession(previewCacheKey);
  }

  private loadSourceDataFromSequencingPoll(): void {
    this.sequencingPollSub?.unsubscribe();
    this.sourceFileAttributeStateByFileId.clear();
    this.sourceFieldSourceDataByFileId.clear();
    this.sourceFieldSourceDataMap = new Map();
    this.clearSourceBoundingBoxHighlight();
    this.annotationDefaultDocumentNameByFileId.clear();
    this.activeAnnotationStateFileId = null;
    this.annotationPanelStateByFileId.clear();
    this.annotationSaveBaselineByPage = {};
    this.annotationSaveBaselineFileId = null;
    this.isAnnotationSaveInProgress = false;
    this.showAttributesSavedDot = false;
    this.pageRotations.clear();
    this.sourceFiles = [];
    this.clearPreviewPdfState();
    this.sourceAttributes = [];
    this.initializeSourceAttributesState();

    if (!this.sourceRequestId) {
      this.sourceDataMessage = 'No sequencing request selected.';
      this.isSourceDataLoading = false;
      return;
    }

    this.isSourceDataLoading = true;
    this.sourceDataMessage = '';

    this.sequencingPollSub = this.truthManagementService.getSequencingPollSequence({
      request_id: this.sourceRequestId,
    }).subscribe({
      next: (response) => {
        this.ngZone.run(() => {
          this.applySequencingPollResponse(response);
          // Auto-select the first document the user sees in the tree — the
          // same path as a click: activates it, loads the PDF once, honours a
          // ?page=N deep-link, scrolls to its first page. The post-save
          // refresh has its own restore path and never reaches here.
          if (!this.selectedFileId && this.sourceFiles.length > 0) {
            const first = this.filteredSourceFiles[0] ?? this.sourceFiles[0];
            this.selectFile(first.id);
          }
          this.isSourceDataLoading = false;
          this.cdr.detectChanges();
        });
      },
      error: () => {
        this.ngZone.run(() => {
          this.sourceFiles = [];
          this.clearPreviewPdfState();
          this.sourceAttributes = [];
          this.initializeSourceAttributesState();
          this.sourceDataMessage = 'Unable to load files and attributes for this request.';
          this.isSourceDataLoading = false;
          this.cdr.detectChanges();
        });
      },
      complete: () => {
        this.ngZone.run(() => {
          this.isSourceDataLoading = false;
          this.cdr.detectChanges();
        });
      },
    });
  }

  private applySequencingPollResponse(response: SequencingPollSequenceResponse): void {
    this.hydrateAnnotationMetaFromPoll(response);

    const status = String(response.status || response.data?.status || '').trim().toUpperCase();
    if (status && status !== 'COMPLETED') {
      this.annotationDefaultDocumentNameByFileId.clear();
      this.activeAnnotationStateFileId = null;
      this.sourceFiles = [];
      this.clearPreviewPdfState();
      this.sourceAttributes = [];
      this.initializeSourceAttributesState();
      this.sourceDataMessage = `Sequencing is ${status}. Files and attributes will appear when it completes.`;
      return;
    }

    const documents = this.getSequencingPollDocuments(response);
    if (documents.length === 0) {
      this.annotationDefaultDocumentNameByFileId.clear();
      this.activeAnnotationStateFileId = null;
      this.sourceFiles = [];
      this.clearPreviewPdfState();
      this.sourceAttributes = [];
      this.initializeSourceAttributesState();
      this.sourceDataMessage = 'No files available for this request.';
      return;
    }

    const seenIds = new Set<string>();
    const mappedFiles: SourceFileItem[] = [];
    this.annotationDefaultDocumentNameByFileId.clear();

    documents.forEach((document, index) => {
      const fileItem = this.mapPollDocumentToFile(document, index, seenIds);
      mappedFiles.push(fileItem);
      this.sourceFileAttributeStateByFileId.set(fileItem.id, this.buildAttributeStateForDocument(document));
      this.annotationDefaultDocumentNameByFileId.set(fileItem.id, fileItem.fileName.trim());
      this.sourceFieldSourceDataByFileId.set(fileItem.id, this.buildFieldSourceDataMapForDocument(document, fileItem));
    });

    this.sourceFiles = mappedFiles;
    this.selectedFileId = '';
    this.clearPreviewPdfState();
    this.sourceFieldSourceDataMap = new Map();
    this.activeAnnotationStateFileId = null;
    this.sourceAttributes = [];
    this.initializeSourceAttributesState();
    this.sourceDataMessage = '';
  }

  private hydrateAnnotationMetaFromPoll(response: SequencingPollSequenceResponse): void {
    const hilStatus = response?.hil_status ?? response?.data?.hil_status;
    this.sourceReviewStatus = this.mapHilStatusToReviewStatus(hilStatus);
    this.sourceReviewStatusBaseline = this.sourceReviewStatus;

    const details = response?.annotation_details ?? response?.data?.annotation_details;
    const rawEdits = details?.docNameEdits ?? {};
    const nextEdits: Record<number, string> = {};
    Object.entries(rawEdits).forEach(([key, value]) => {
      const page = Number(key);
      if (Number.isFinite(page) && typeof value === 'string' && value.trim()) {
        nextEdits[page] = value;
      }
    });
    this.editedDocNameByPage = nextEdits;

    const rawComments = details?.comments ?? {};
    const nextComments: Record<number, string> = {};
    Object.entries(rawComments).forEach(([key, value]) => {
      const page = Number(key);
      if (Number.isFinite(page) && typeof value === 'string') {
        nextComments[page] = value;
      }
    });
    this.existingCommentsByPage = nextComments;
  }

  private getSequencingPollDocuments(response: SequencingPollSequenceResponse): SequencingPollDocument[] {
    const documentsFromData = response.data?.documents;
    if (Array.isArray(documentsFromData)) {
      return documentsFromData;
    }

    if (Array.isArray(response.documents)) {
      return response.documents;
    }

    return [];
  }

  private mapPollDocumentToFile(document: SequencingPollDocument, index: number, seenIds: Set<string>): SourceFileItem {
    const mappedDocumentName = this.getFirstDefinedString([
      document['Mapped Document Name'],
      document.document,
      document.doc_type,
      document.document_name,
      document['Document Name'],
      document.file_name,
    ]) || `Document ${index + 1}`;

    const documentName = this.getFirstDefinedString([
      document['Document Name'],
      document.document_name,
      document['Base File Name'],
      document.base_file_name,
      document.file_name,
      mappedDocumentName,
    ]) || mappedDocumentName;

    const rawMatchType = this.getFirstDefinedString([
      document['Match Type'],
    ]);
    const matchTypeCode = this.normalizeMatchTypeCode(rawMatchType);
    const matchTypeMeta = this.getMatchTypeMeta(matchTypeCode);

    const startPageNumber = this.parseOptionalNumber(document['Start Page Number']);
    const endPageNumber = this.parseOptionalNumber(document['End Page Number']);
    const totalDocumentPages = this.getTotalDocumentPages(startPageNumber, endPageNumber);

    const rawId = this.getFirstDefinedString([
      document.id != null ? String(document.id) : '',
      document.file_name,
      document.base_file_name,
      documentName,
    ]) || `file-${index + 1}`;

    const normalizedBaseId = rawId.trim().replace(/\s+/g, '-').toLowerCase() || `file-${index + 1}`;
    let uniqueId = normalizedBaseId;
    let suffix = 2;
    while (seenIds.has(uniqueId)) {
      uniqueId = `${normalizedBaseId}-${suffix}`;
      suffix += 1;
    }
    seenIds.add(uniqueId);

    return {
      id: uniqueId,
      fileName: mappedDocumentName,
      subFileName: documentName,
      version: this.getFirstDefinedString([document.version]) || '',
      matchTypeCode,
      matchTypeLabel: matchTypeMeta.label,
      matchTypeHowDetermined: matchTypeMeta.howDetermined,
      matchTypeBadgeClass: matchTypeMeta.badgeClass,
      templateId: this.resolveTemplateIdForDocument(document, matchTypeCode, startPageNumber),
      startPageNumber,
      endPageNumber,
      totalDocumentPages,
      pageRangeSummary: this.buildPageRangeSummary(startPageNumber, endPageNumber, totalDocumentPages),
    };
  }

  private parseOptionalNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim().length > 0) {
      const parsed = Number.parseInt(value.trim(), 10);
      return Number.isNaN(parsed) ? null : parsed;
    }

    return null;
  }

  private getTotalDocumentPages(startPageNumber: number | null, endPageNumber: number | null): number | null {
    if (startPageNumber === null || endPageNumber === null || endPageNumber < startPageNumber) {
      return null;
    }

    return (endPageNumber - startPageNumber) + 1;
  }

  private buildPageRangeSummary(startPageNumber: number | null, endPageNumber: number | null, totalDocumentPages: number | null): string {
    if (startPageNumber === null || endPageNumber === null || totalDocumentPages === null) {
      return 'Pages -';
    }

    return `Pages ${startPageNumber} to ${endPageNumber} (${totalDocumentPages})`;
  }

  private resolveTemplateIdForDocument(document: SequencingPollDocument, matchTypeCode: string, startPageNumber: number | null): string {
    if (matchTypeCode !== 'TPL' || startPageNumber === null) {
      return '';
    }

    const pageTemplates = document.page_templates;
    if (!pageTemplates || typeof pageTemplates !== 'object') {
      return '';
    }

    const templateInfo = pageTemplates[String(startPageNumber)];
    if (!templateInfo) {
      return '';
    }

    if (Array.isArray(templateInfo)) {
      const firstTemplate = templateInfo.find((entry) => !!entry && typeof entry === 'object');
      const templateId = firstTemplate?.template_id;
      return templateId == null ? '' : String(templateId).trim();
    }

    const templateId = templateInfo.template_id;
    return templateId == null ? '' : String(templateId).trim();
  }

  private getMatchTypeMeta(matchTypeCode: string): SourceMatchTypeMeta {
    if (!matchTypeCode) {
      return {
        label: 'Unknown',
        howDetermined: 'Match type was not provided by backend',
        badgeClass: 'match-unknown',
      };
    }

    return SOURCE_MATCH_TYPE_META_BY_CODE[matchTypeCode] || {
      label: matchTypeCode,
      howDetermined: 'Match type provided by backend (definition unavailable)',
      badgeClass: 'match-unknown',
    };
  }

  private applyZoomWithCenterAnchor(nextZoom: number): boolean {
    const viewer = this.getPdfViewerScrollContainer();
    const centerX = viewer ? viewer.clientWidth / 2 : 0;
    const centerY = viewer ? viewer.clientHeight / 2 : 0;
    return this.applyZoomWithAnchor(nextZoom, centerX, centerY);
  }

  private applyZoomWithAnchor(nextZoom: number, anchorOffsetX: number, anchorOffsetY: number): boolean {
    const viewer = this.getPdfViewerScrollContainer();
    const boundedOffsetX = viewer ? this.clamp(anchorOffsetX, 0, viewer.clientWidth) : 0;
    const boundedOffsetY = viewer ? this.clamp(anchorOffsetY, 0, viewer.clientHeight) : 0;
    const ratioX = viewer && viewer.scrollWidth > 0
      ? (viewer.scrollLeft + boundedOffsetX) / viewer.scrollWidth
      : 0;
    const ratioY = viewer && viewer.scrollHeight > 0
      ? (viewer.scrollTop + boundedOffsetY) / viewer.scrollHeight
      : 0;

    const previousZoom = this.zoom;
    this.updateZoom(nextZoom);
    if (this.zoom === previousZoom) {
      return false;
    }

    this.cdr.detectChanges();
    this.restorePreviewZoomAnchor(ratioX, ratioY, boundedOffsetX, boundedOffsetY);
    return true;
  }

  private restorePreviewZoomAnchor(ratioX: number, ratioY: number, anchorOffsetX: number, anchorOffsetY: number): void {
    this.cancelPendingPreviewZoomRestore();

    const restore = () => {
      const viewer = this.getPdfViewerScrollContainer();
      if (!viewer) {
        return;
      }

      const nextLeft = (ratioX * viewer.scrollWidth) - anchorOffsetX;
      const nextTop = (ratioY * viewer.scrollHeight) - anchorOffsetY;
      const maxScrollLeft = Math.max(0, viewer.scrollWidth - viewer.clientWidth);
      const maxScrollTop = Math.max(0, viewer.scrollHeight - viewer.clientHeight);

      viewer.scrollLeft = this.clamp(nextLeft, 0, maxScrollLeft);
      viewer.scrollTop = this.clamp(nextTop, 0, maxScrollTop);
    };

    this.previewZoomRestoreFrame = requestAnimationFrame(() => {
      this.previewZoomRestoreFrame = null;
      restore();
    });
    // pdf.js applies the new scale asynchronously; re-apply so late layout nudges don't move the anchor.
    this.previewZoomRestoreTimeouts = [setTimeout(restore, 40), setTimeout(restore, 120)];
  }

  private cancelPendingPreviewZoomRestore(): void {
    if (this.previewZoomRestoreFrame !== null) {
      cancelAnimationFrame(this.previewZoomRestoreFrame);
      this.previewZoomRestoreFrame = null;
    }

    this.previewZoomRestoreTimeouts.forEach((timeout) => clearTimeout(timeout));
    this.previewZoomRestoreTimeouts = [];
  }

  private updateZoom(nextZoom: number): void {
    const previousZoom = this.zoom;
    const clampedZoom = this.clamp(nextZoom, this.minZoom, this.maxZoom);
    this.zoom = Number(clampedZoom.toFixed(2));
    this.zoomPercentage = Math.round(this.zoom * 100);

    if (this.zoom === previousZoom) {
      return;
    }

    // Re-fit the pages at the new zoom, but do NOT scroll here: every caller
    // comes through applyZoomWithAnchor, whose restorePreviewZoomAnchor keeps
    // the point under the cursor / viewport centre in place. relayoutPages
    // would first jump to the page top and fight it. The bbox refresh below
    // re-measures against the new layout.
    this.computePageLayout();
    this.scheduleSourceBoundingBoxReRender();
  }

  private scheduleSourceBoundingBoxReRender(): void {
    if (this.sourceBoundingBoxReRenderTimer !== null) {
      clearTimeout(this.sourceBoundingBoxReRenderTimer);
    }

    // pdf.js rescales pages asynchronously and drops injected children; wait for it to settle.
    this.sourceBoundingBoxReRenderTimer = setTimeout(() => {
      this.sourceBoundingBoxReRenderTimer = null;
      this.refreshActiveSourceBoundingBoxesAfterZoom();
    }, 300);
  }

  private refreshActiveSourceBoundingBoxesAfterZoom(): void {
    if (this.highlightedSourceAttributeId === null) {
      return;
    }

    const sourceData = this.sourceFieldSourceDataMap.get(this.highlightedSourceAttributeId);
    if (!sourceData) {
      return;
    }

    this.renderSourceBoundingBoxOverlaysWithRetry(sourceData, 0);
  }

  private normalizeMatchTypeCode(matchType: string): string {
    const normalized = matchType.trim().toUpperCase();
    if (!normalized) {
      return '';
    }

    if (SOURCE_MATCH_TYPE_META_BY_CODE[normalized]) {
      return normalized;
    }

    if (normalized === 'TEMPLATE' || normalized === 'TAMPLATE' || normalized === 'TEMPALTE') {
      return 'TPL';
    }

    return normalized;
  }

  private buildAttributeStateForDocument(document: SequencingPollDocument): SourceFileAttributeState {
    const extractedFields = document['Extracted Fields'] || document.extracted_fields;
    const entries = extractedFields ? Object.entries(extractedFields) : [];

    const attributes: SourceAttributeItem[] = [];
    const values: Record<number, string[]> = {};
    const originalValues: Record<number, string[]> = {};
    const notApplicable = new Set<number>();
    const originalNotApplicable = new Set<number>();

    entries.forEach(([attributeLabel, rawField], index) => {
      const attributeId = index + 1;

      let normalizedValues: string[];
      let isArray: boolean;
      let field: SequencingPollExtractedField | undefined;

      if (Array.isArray(rawField)) {
        const fieldArray = rawField as SequencingPollExtractedField[];
        normalizedValues = fieldArray.length > 0
          ? fieldArray.map(item => this.normalizeSinglePollValue(item?.Value))
          : [''];
        isArray = fieldArray.length > 1;
        field = fieldArray[0];
      } else {
        field = rawField as SequencingPollExtractedField;
        normalizedValues = this.normalizePollFieldValues(field);
        isArray = Array.isArray(field?.Value);
      }

      const item: SourceAttributeItem = {
        id: attributeId,
        attributeLabel,
        description: this.getFirstDefinedString([
          field?.['Extracted Text'] as string,
          field?.Section as string,
          field?.Reasoning as string,
        ]) || 'Extracted from sequencing output.',
        fieldType: this.inferSourceFieldTypeFromValues(normalizedValues),
        isArray,
        initialValues: [...normalizedValues],
      };

      attributes.push(item);
      values[attributeId] = [...normalizedValues];
      originalValues[attributeId] = [...normalizedValues];

      if (this.areAllValuesNotApplicable(normalizedValues)) {
        notApplicable.add(attributeId);
        originalNotApplicable.add(attributeId);
      }
    });

    return {
      attributes,
      values,
      originalValues,
      notApplicable,
      originalNotApplicable,
    };
  }

  private buildFieldSourceDataMapForDocument(document: SequencingPollDocument, file: SourceFileItem): Map<number, SourceFieldSourceData> {
    const extractedFields = document['Extracted Fields'] || document.extracted_fields;
    const entries = extractedFields ? Object.entries(extractedFields) : [];
    const map = new Map<number, SourceFieldSourceData>();

    entries.forEach(([, rawField], index) => {
      const attributeId = index + 1;
      let normalized: SourceFieldSourceData | null;

      if (Array.isArray(rawField)) {
        normalized = this.normalizeSourceBoundingBoxDataFromArray(
          rawField as SequencingPollExtractedField[],
          file.startPageNumber,
          this.resolveAnnotationPageCountForFile(file),
        );
      } else {
        normalized = this.normalizeSourceBoundingBoxData(
          rawField as SequencingPollExtractedField | undefined,
          file.startPageNumber,
          this.resolveAnnotationPageCountForFile(file),
        );
      }

      if (normalized) {
        map.set(attributeId, normalized);
      }
    });

    return map;
  }

  private normalizeSourceBoundingBoxData(
    field: SequencingPollExtractedField | undefined,
    fileStartPageNumber: number | null,
    filePageCount: number,
  ): SourceFieldSourceData | null {
    if (!field) {
      return null;
    }

    const record = field as SequencingPollExtractedField & Record<string, unknown>;
    const pageWidth = this.parseNumericValue(record['Page Width']) || 612;
    const rawPage = record['Page Number'];
    const rawBoundingBox = record.BoundingBox;

    let pageNumber = 0;
    const boundingBoxes: number[][] = [];
    const boundingBoxIndices: number[][] = [];

    if (Array.isArray(rawPage) && Array.isArray(rawBoundingBox) && Array.isArray(rawBoundingBox[0])) {
      const alignedLength = Math.min(rawPage.length, rawBoundingBox.length);
      for (let index = 0; index < alignedLength; index += 1) {
        const parsedPage = this.parseNumericValue(rawPage[index]);
        const box = this.parseBoundingBox(rawBoundingBox[index]);
        if (!box) {
          continue;
        }

        if (parsedPage && parsedPage > 0 && !pageNumber) {
          pageNumber = parsedPage;
        }

        boundingBoxes.push(box);
        boundingBoxIndices.push([index + 1]);
      }
    } else {
      const parsedPage = this.parsePageNumber(rawPage);
      if (parsedPage > 0) {
        pageNumber = parsedPage;
      }

      if (Array.isArray(rawBoundingBox) && Array.isArray(rawBoundingBox[0])) {
        (rawBoundingBox as unknown[]).forEach((boxValue, index) => {
          const box = this.parseBoundingBox(boxValue);
          if (!box) {
            return;
          }
          boundingBoxes.push(box);
          boundingBoxIndices.push([index + 1]);
        });
      } else {
        const singleBox = this.parseBoundingBox(rawBoundingBox);
        if (singleBox) {
          boundingBoxes.push(singleBox);
          boundingBoxIndices.push([1]);
        }
      }
    }

    if (boundingBoxes.length === 0) {
      const fallbackBox = this.parseBoundingBoxFromCorners(record);
      if (fallbackBox) {
        boundingBoxes.push(fallbackBox);
        boundingBoxIndices.push([1]);
      }
    }

    if (pageNumber > 0) {
      pageNumber = this.mapApiPageToLocalFilePage(pageNumber, fileStartPageNumber, filePageCount);
    }

    if (!pageNumber && boundingBoxes.length > 0) {
      pageNumber = 1;
    }

    if (!pageNumber && boundingBoxes.length === 0) {
      return null;
    }

    return {
      pageNumber,
      boundingBoxes,
      pageWidth,
      boundingBoxIndices: boundingBoxIndices.length ? boundingBoxIndices : undefined,
    };
  }

  private normalizeSourceBoundingBoxDataFromArray(
    fieldArray: SequencingPollExtractedField[],
    fileStartPageNumber: number | null,
    filePageCount: number,
  ): SourceFieldSourceData | null {
    if (!fieldArray.length) {
      return null;
    }

    const firstRecord = fieldArray[0] as SequencingPollExtractedField & Record<string, unknown>;
    const pageWidth = this.parseNumericValue(firstRecord['Page Width']) || 612;
    let pageNumber = 0;
    const boundingBoxes: number[][] = [];
    const boundingBoxIndices: number[][] = [];

    fieldArray.forEach((element, index) => {
      const record = element as SequencingPollExtractedField & Record<string, unknown>;
      const parsedPage = this.parsePageNumber(record['Page Number']);
      if (parsedPage > 0 && !pageNumber) {
        pageNumber = parsedPage;
      }

      const box = this.parseBoundingBox(record.BoundingBox) || this.parseBoundingBoxFromCorners(record);
      if (box) {
        boundingBoxes.push(box);
        boundingBoxIndices.push([index + 1]);
      }
    });

    if (pageNumber > 0) {
      pageNumber = this.mapApiPageToLocalFilePage(pageNumber, fileStartPageNumber, filePageCount);
    }

    if (!pageNumber && boundingBoxes.length > 0) {
      pageNumber = 1;
    }

    if (!pageNumber && boundingBoxes.length === 0) {
      return null;
    }

    return {
      pageNumber,
      boundingBoxes,
      pageWidth,
      boundingBoxIndices: boundingBoxIndices.length ? boundingBoxIndices : undefined,
    };
  }

  private mapApiPageToLocalFilePage(pageNumber: number, fileStartPageNumber: number | null, filePageCount: number): number {
    if (fileStartPageNumber !== null) {
      const mapped = (pageNumber - fileStartPageNumber) + 1;
      if (mapped >= 1 && mapped <= filePageCount) {
        return mapped;
      }
    }

    if (pageNumber >= 1 && pageNumber <= filePageCount) {
      return pageNumber;
    }

    return Math.min(filePageCount, Math.max(1, pageNumber));
  }

  private parsePageNumber(rawPage: unknown): number {
    if (Array.isArray(rawPage)) {
      for (const value of rawPage) {
        const parsed = this.parseNumericValue(value);
        if (parsed && parsed > 0) {
          return parsed;
        }
      }
      return 0;
    }

    return this.parseNumericValue(rawPage) || 0;
  }

  private parseNumericValue(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return Math.floor(value);
    }

    if (typeof value === 'string') {
      const parsed = Number.parseFloat(value.trim());
      return Number.isFinite(parsed) ? Math.floor(parsed) : null;
    }

    return null;
  }

  private parseBoundingBox(rawBoundingBox: unknown): number[] | null {
    if (!Array.isArray(rawBoundingBox) || rawBoundingBox.length < 4) {
      return null;
    }

    const a = this.parseCoordinate(rawBoundingBox[0]);
    const b = this.parseCoordinate(rawBoundingBox[1]);
    const c = this.parseCoordinate(rawBoundingBox[2]);
    const d = this.parseCoordinate(rawBoundingBox[3]);

    if (a === null || b === null || c === null || d === null) {
      return null;
    }

    const x0 = Math.min(a, c);
    const y0 = Math.min(b, d);
    const x1 = Math.max(a, c);
    const y1 = Math.max(b, d);

    if (x1 === x0 && y1 === y0) {
      return null;
    }

    return [x0, y0, x1, y1];
  }

  private parseBoundingBoxFromCorners(record: Record<string, unknown>): number[] | null {
    const x0 = this.parseCoordinate(record['TopLeftX']);
    const y0 = this.parseCoordinate(record['TopLeftY']);
    const x1 = this.parseCoordinate(record['BottomRightX']);
    const y1 = this.parseCoordinate(record['BottomRightY']);

    if (x0 === null || y0 === null || x1 === null || y1 === null) {
      return null;
    }

    if (x1 <= x0 || y1 <= y0) {
      return null;
    }

    return [x0, y0, x1, y1];
  }

  private parseCoordinate(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = Number.parseFloat(value.trim());
      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  }

  private renderSourceBoundingBoxOverlaysWithRetry(sourceData: SourceFieldSourceData, retryCount: number): void {
    const maxRetries = 20;
    const retryDelayMs = 150;
    const pageElement = this.getRenderedSourcePageElement(this.toAbsolutePreviewPage(sourceData.pageNumber));
    const isPageReady = !!pageElement && pageElement.offsetWidth > 0 && pageElement.offsetHeight > 0;

    if (!isPageReady) {
      if (retryCount >= maxRetries) {
        return;
      }

      setTimeout(() => {
        this.renderSourceBoundingBoxOverlaysWithRetry(sourceData, retryCount + 1);
      }, retryDelayMs);
      return;
    }

    this.renderSourceBoundingBoxOverlays(sourceData, pageElement);
  }

  /**
   * @param recenter Smooth-scroll the viewport so the first box is centred.
   *   True for an attribute click; false when re-injecting after CDK recreated
   *   the page element on scroll-back, so we never fight the user's scroll.
   */
  private renderSourceBoundingBoxOverlays(
    sourceData: SourceFieldSourceData,
    pageElement: HTMLElement,
    recenter = true,
  ): void {
    this.clearSourceBoundingBoxOverlayElements();
    this.activeSourceBoundingBoxes = [];

    if (!sourceData.boundingBoxes.length) {
      return;
    }

    if (getComputedStyle(pageElement).position === 'static') {
      pageElement.style.position = 'relative';
    }

    const renderedWidth = pageElement.offsetWidth;
    const absolutePage = this.toAbsolutePreviewPage(sourceData.pageNumber);
    // The API's `Page Width` is the reference frame its `BoundingBox` coords
    // live in — prefer it. Fall back to the pdf.js-derived size (usable when
    // the API omitted Page Width), then 612 as a last resort.
    const serverPageWidth = this.sourcePageSizesFromServer[absolutePage - 1]?.width;
    const pageWidth = (sourceData.pageWidth > 0)
      ? sourceData.pageWidth
      : ((serverPageWidth && serverPageWidth > 0) ? serverPageWidth : 612);
    const scale = renderedWidth / pageWidth;
    const highlightedAttribute = this.highlightedSourceAttributeId !== null
      ? this.getSourceAttribute(this.highlightedSourceAttributeId)
      : undefined;
    const showIndexBadges = highlightedAttribute?.isArray === true;
    const indicesMap = showIndexBadges
      ? (sourceData.boundingBoxIndices || sourceData.boundingBoxes.map((_, index) => [index + 1]))
      : sourceData.boundingBoxes.map(() => []);

    const BOUNDING_BOX_PADDING_RATIO = 0.2;

    sourceData.boundingBoxes.forEach((bbox, index) => {
      if (bbox.length < 4) {
        return;
      }

      const rawWidth = (bbox[2] - bbox[0]) * scale;
      const rawHeight = (bbox[3] - bbox[1]) * scale;
      const padX = rawWidth * (BOUNDING_BOX_PADDING_RATIO / 2);
      const padY = rawHeight * (BOUNDING_BOX_PADDING_RATIO / 2);

      const overlay: SourceOverlayBox = {
        left: bbox[0] * scale - padX,
        top: bbox[1] * scale - padY,
        width: rawWidth + padX * 2,
        height: rawHeight + padY * 2,
        indices: indicesMap[index] || [index + 1],
      };

      this.activeSourceBoundingBoxes.push(overlay);
      this.createSourceBoundingBoxOverlayElement(pageElement, overlay);
    });

    if (!recenter) {
      return;
    }
    requestAnimationFrame(() => {
      this.scrollToSourceBoundingBox();
    });
  }

  private createSourceBoundingBoxOverlayElement(container: HTMLElement, overlay: SourceOverlayBox): void {
    const overlayElement = document.createElement('div');
    overlayElement.className = 'source-bounding-box-overlay source-injected-bbox';
    overlayElement.style.position = 'absolute';
    overlayElement.style.left = `${overlay.left}px`;
    overlayElement.style.top = `${overlay.top}px`;
    overlayElement.style.width = `${overlay.width}px`;
    overlayElement.style.height = `${overlay.height}px`;
    overlayElement.style.backgroundColor = 'rgba(37, 99, 235, 0.12)';
    overlayElement.style.border = '1px solid rgba(37, 99, 235, 0.9)';
    overlayElement.style.borderRadius = '2px';
    overlayElement.style.pointerEvents = 'none';
    overlayElement.style.zIndex = '100';
    overlayElement.style.boxShadow = '0 2px 8px rgba(37, 99, 235, 0.15)';

    if (overlay.indices.length > 0) {
      const badge = document.createElement('span');
      badge.className = 'source-bbox-number';
      badge.textContent = overlay.indices.join(', ');
      badge.style.position = 'absolute';
      badge.style.top = '-16px';
      badge.style.left = '0px';
      badge.style.height = '14px';
      badge.style.padding = '0 4px';
      badge.style.background = '#2563eb';
      badge.style.color = 'white';
      badge.style.borderRadius = '2px';
      badge.style.fontSize = '10px';
      badge.style.fontWeight = '600';
      badge.style.display = 'flex';
      badge.style.alignItems = 'center';
      badge.style.justifyContent = 'center';
      badge.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.2)';
      badge.style.whiteSpace = 'nowrap';
      overlayElement.appendChild(badge);
    }

    container.appendChild(overlayElement);
  }

  private clearSourceBoundingBoxOverlayElements(): void {
    const container = this.previewScroller?.nativeElement;
    if (!container) {
      return;
    }

    const existing = container.querySelectorAll('.source-injected-bbox');
    existing.forEach((element) => element.remove());
  }

  private getRenderedSourcePageElement(pageNumber: number): HTMLElement | null {
    const container = this.previewScroller?.nativeElement;
    if (!container) {
      return null;
    }
    // Match the new PDF.js lazy-load pipeline's placeholder DOM. Wrapper is
    // the offsetParent while `.rendered` is present, so bounding-box overlay
    // math using offsetTop/offsetLeft still works.
    return container.querySelector<HTMLElement>(`.pdf-page-wrapper[data-page="${pageNumber}"]`);
  }

  private getSourceBoundingBoxTargetTop(sourceData: SourceFieldSourceData, container: HTMLElement): number | null {
    const pageElement = this.getRenderedSourcePageElement(this.toAbsolutePreviewPage(sourceData.pageNumber));
    if (!pageElement) {
      return null;
    }

    const injectedOverlay = pageElement.querySelector('.source-injected-bbox') as HTMLElement | null;
    let absoluteCenter: number;

    if (injectedOverlay) {
      const containerRect = container.getBoundingClientRect();
      const overlayRect = injectedOverlay.getBoundingClientRect();
      const overlayTopInContainer = overlayRect.top - containerRect.top + container.scrollTop;
      absoluteCenter = overlayTopInContainer + (overlayRect.height / 2);
    } else {
      const firstBox = this.activeSourceBoundingBoxes[0];
      if (!firstBox) {
        return null;
      }

      // Measure via rects: under CDK virtual scroll the page's offsetParent is
      // the translated content wrapper, so offsetTop is not container-relative.
      const containerRect = container.getBoundingClientRect();
      const pageRect = pageElement.getBoundingClientRect();
      const pageTopInContainer = pageRect.top - containerRect.top + container.scrollTop;
      const absoluteTop = pageTopInContainer + firstBox.top;
      absoluteCenter = absoluteTop + (firstBox.height / 2);
    }

    const rawTargetTop = absoluteCenter - (container.clientHeight / 2);
    const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
    return Math.min(maxScrollTop, Math.max(0, rawTargetTop));
  }

  private scrollToSourceBoundingBox(): void {
    if (!this.activeSourceBoundingBoxes.length || this.highlightedSourceAttributeId === null) {
      return;
    }

    const highlightedAttributeId = this.highlightedSourceAttributeId;
    const sourceData = this.sourceFieldSourceDataMap.get(highlightedAttributeId);
    const container = this.getPdfViewerScrollContainer();
    if (!sourceData || !container) {
      return;
    }

    const targetTop = this.getSourceBoundingBoxTargetTop(sourceData, container);
    if (targetTop === null) {
      return;
    }

    // Suppress the file-swap side-effect of scrolledIndexChange while the
    // smooth scroll animates through pages that belong to neighbouring files.
    // Reuses the same guard computePageLayout/scrollToPage already use.
    this.ignoreScrollIndexUntilProgrammaticScroll = true;
    if (this.sourceBoundingBoxSuppressionTimeout !== null) {
      clearTimeout(this.sourceBoundingBoxSuppressionTimeout);
      this.sourceBoundingBoxSuppressionTimeout = null;
    }

    container.scrollTo({
      top: targetTop,
      behavior: 'smooth',
    });

    if (this.sourceBoundingBoxRecenterTimeout !== null) {
      clearTimeout(this.sourceBoundingBoxRecenterTimeout);
      this.sourceBoundingBoxRecenterTimeout = null;
    }

    // One follow-up correction after layout settles prevents intermittent off-center stops.
    this.sourceBoundingBoxRecenterTimeout = setTimeout(() => {
      if (this.highlightedSourceAttributeId !== highlightedAttributeId) {
        this.sourceBoundingBoxRecenterTimeout = null;
        this.releaseSourceBoundingBoxScrollSuppression(0);
        return;
      }

      const correctedTargetTop = this.getSourceBoundingBoxTargetTop(sourceData, container);
      if (correctedTargetTop !== null) {
        const correctionDelta = Math.abs(correctedTargetTop - container.scrollTop);
        if (correctionDelta > 8) {
          container.scrollTo({
            top: correctedTargetTop,
            behavior: 'smooth',
          });
        }
      }

      this.sourceBoundingBoxRecenterTimeout = null;
      this.releaseSourceBoundingBoxScrollSuppression(600);
    }, 260);
  }

  private releaseSourceBoundingBoxScrollSuppression(delayMs: number): void {
    if (this.sourceBoundingBoxSuppressionTimeout !== null) {
      clearTimeout(this.sourceBoundingBoxSuppressionTimeout);
    }
    this.sourceBoundingBoxSuppressionTimeout = setTimeout(() => {
      this.ignoreScrollIndexUntilProgrammaticScroll = false;
      this.sourceBoundingBoxSuppressionTimeout = null;
    }, delayMs);
  }

  private clearSourceBoundingBoxHighlight(): void {
    if (this.sourceBoundingBoxRecenterTimeout !== null) {
      clearTimeout(this.sourceBoundingBoxRecenterTimeout);
      this.sourceBoundingBoxRecenterTimeout = null;
    }
    if (this.sourceBoundingBoxSuppressionTimeout !== null) {
      clearTimeout(this.sourceBoundingBoxSuppressionTimeout);
      this.sourceBoundingBoxSuppressionTimeout = null;
    }
    this.ignoreScrollIndexUntilProgrammaticScroll = false;

    this.highlightedSourceAttributeId = null;
    this.activeSourceBoundingBoxes = [];
    this.clearSourceBoundingBoxOverlayElements();
  }

  private normalizePollFieldValues(field: SequencingPollExtractedField | undefined): string[] {
    if (!field) {
      return [''];
    }

    const rawValue = field.Value;
    if (Array.isArray(rawValue)) {
      const normalized = rawValue.map((value) => this.normalizeSinglePollValue(value));
      return normalized.length > 0 ? normalized : [''];
    }

    return [this.normalizeSinglePollValue(rawValue)];
  }

  private normalizeSinglePollValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    const normalized = String(value).trim();
    const isoDateMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoDateMatch) {
      return `${isoDateMatch[2]}/${isoDateMatch[3]}/${isoDateMatch[1]}`;
    }

    return normalized;
  }

  private inferSourceFieldTypeFromValues(values: string[]): SourceAttributeFieldType {
    const nonEmptyValues = values.map((value) => value.trim()).filter((value) => value.length > 0);
    if (!nonEmptyValues.length) {
      return 'text';
    }

    const areAllDates = nonEmptyValues.every((value) => /^\d{2}\/\d{2}\/\d{4}$/.test(value));
    return areAllDates ? 'date' : 'text';
  }

  private areAllValuesNotApplicable(values: string[]): boolean {
    const normalized = values.map((value) => value.trim().toLowerCase()).filter((value) => value.length > 0);
    return normalized.length > 0 && normalized.every((value) => value === 'na' || value === 'n/a');
  }

  private getFirstDefinedString(values: Array<string | undefined | null>): string {
    for (const value of values) {
      if (typeof value !== 'string') {
        continue;
      }

      const trimmed = value.trim();
      if (trimmed.length > 0) {
        return trimmed;
      }
    }

    return '';
  }

  private commitPopupSelection(): void {
    if (!this.activeDateTimePopup || !this.popupDate) {
      return;
    }

    const datePart = this.formatDatePartFromDate(this.popupDate);
    const localDateTime = this.buildLocalDateTime(datePart, this.popupHour12, this.popupMinute, this.popupPeriod);
    const currentValue = this.activeDateTimePopup === 'start' ? this.startDate : this.endDate;

    if (localDateTime === currentValue) {
      this.syncDateDisplayValues();
      this.updateEndDateInvalidState();
      return;
    }

    if (this.activeDateTimePopup === 'start') {
      this.startDate = localDateTime;
      this.startHour12 = this.popupHour12;
      this.startMinute = this.popupMinute;
      this.startPeriod = this.popupPeriod;
    } else {
      this.endDate = localDateTime;
      this.endHour12 = this.popupHour12;
      this.endMinute = this.popupMinute;
      this.endPeriod = this.popupPeriod;
    }

    this.syncDateDisplayValues();
    this.selectedTimeFilter = 'custom';
    this.popupDirty = true;
    this.updateEndDateInvalidState();
  }

  private removeCalendarNavTitles(): void {
    setTimeout(() => {
      const navButtons = document.querySelectorAll('.date-time-popup-panel .mat-calendar-previous-button, .date-time-popup-panel .mat-calendar-next-button');
      navButtons.forEach((button) => {
        if (button instanceof HTMLElement) {
          button.removeAttribute('title');
        }
      });
    }, 0);
  }

  private loadSharedDateFilterState(): SharedDateFilterState | null {
    const raw = sessionStorage.getItem(SHARED_DATE_FILTER_KEY);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as Partial<SharedDateFilterState>;
      const selected = parsed.selectedTimeFilter;
      if (!selected || !['today', 'last7', 'last30', 'custom'].includes(selected)) {
        return null;
      }

      const start = typeof parsed.startDate === 'string' ? parsed.startDate : '';
      const end = typeof parsed.endDate === 'string' ? parsed.endDate : '';
      if (!this.parseLocalDateTime(start) || !this.parseLocalDateTime(end)) {
        return null;
      }

      return {
        selectedTimeFilter: selected as TimeFilter,
        startDate: start,
        endDate: end,
        startDateDisplay: typeof parsed.startDateDisplay === 'string' ? parsed.startDateDisplay : this.formatDateForDisplay(start),
        endDateDisplay: typeof parsed.endDateDisplay === 'string' ? parsed.endDateDisplay : this.formatDateForDisplay(end),
        updatedAt: typeof parsed.updatedAt === 'number' ? parsed.updatedAt : Date.now(),
      };
    } catch {
      return null;
    }
  }

  private applySharedDateFilterState(state: SharedDateFilterState): void {
    this.selectedTimeFilter = state.selectedTimeFilter;
    this.startDate = state.startDate;
    this.endDate = state.endDate;
    this.startDateDisplay = state.startDateDisplay || this.formatDateForDisplay(state.startDate);
    this.endDateDisplay = state.endDateDisplay || this.formatDateForDisplay(state.endDate);
    this.syncTimePartsFromDateTimes();
    this.updateEndDateInvalidState();
  }

  private saveSharedDateFilterState(): void {
    const payload: SharedDateFilterState = {
      selectedTimeFilter: this.selectedTimeFilter,
      startDate: this.startDate,
      endDate: this.endDate,
      startDateDisplay: this.startDateDisplay,
      endDateDisplay: this.endDateDisplay,
      updatedAt: Date.now(),
    };

    sessionStorage.setItem(SHARED_DATE_FILTER_KEY, JSON.stringify(payload));
  }

  private formatDateTimeForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  private parseLocalDateTime(value: string): Date | null {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
    if (!match) {
      return null;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const hours = Number(match[4]);
    const minutes = Number(match[5]);

    const date = new Date(year, month - 1, day, hours, minutes, 0, 0);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return null;
    }

    return date;
  }

  private formatDateTimeForDisplay(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const isPm = date.getHours() >= 12;
    const hour12 = date.getHours() % 12 || 12;
    const period = isPm ? 'PM' : 'AM';
    return `${month}/${day}/${year} ${String(hour12).padStart(2, '0')}:${minutes} ${period}`;
  }

  private buildLocalDateTime(datePart: string, hour12: string, minute: string, period: 'AM' | 'PM'): string {
    const converted = this.convertDisplayToLocalDateTime(`${this.toDisplayDate(datePart)} ${hour12}:${minute} ${period}`);
    if (converted) {
      return converted;
    }

    const hour24 = period === 'PM' ? (Number(hour12) % 12) + 12 : Number(hour12) % 12;
    return `${datePart}T${String(hour24).padStart(2, '0')}:${minute}`;
  }

  private toDisplayDate(datePart: string): string {
    const parts = datePart.split('-');
    if (parts.length !== 3) {
      return datePart;
    }

    return `${parts[1]}/${parts[2]}/${parts[0]}`;
  }

  private formatDatePartFromDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private syncTimePartsFromDateTimes(): void {
    const start = this.parseLocalDateTime(this.startDate);
    if (start) {
      const startIsPm = start.getHours() >= 12;
      this.startPeriod = startIsPm ? 'PM' : 'AM';
      this.startHour12 = String(start.getHours() % 12 || 12).padStart(2, '0');
      this.startMinute = String(start.getMinutes()).padStart(2, '0');
    }

    const end = this.parseLocalDateTime(this.endDate);
    if (end) {
      const endIsPm = end.getHours() >= 12;
      this.endPeriod = endIsPm ? 'PM' : 'AM';
      this.endHour12 = String(end.getHours() % 12 || 12).padStart(2, '0');
      this.endMinute = String(end.getMinutes()).padStart(2, '0');
    }
  }

  private formatDateForDisplay(dateValue: string): string {
    if (!dateValue) {
      return '';
    }

    const date = this.parseLocalDateTime(dateValue);
    if (!date) {
      return '';
    }

    return this.formatDateTimeForDisplay(date);
  }

  private syncDateDisplayValues(): void {
    this.startDateDisplay = this.formatDateForDisplay(this.startDate);
    this.endDateDisplay = this.formatDateForDisplay(this.endDate);
  }

  private normalizeDisplayDateTimeInput(value: string): string {
    return value.replace(/\s+/g, ' ').toUpperCase();
  }

  private convertDisplayToLocalDateTime(value: string): string | null {
    const trimmed = value.trim().toUpperCase();
    const match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})\s*(AM|PM))?$/);
    if (!match) {
      return null;
    }

    const month = Number(match[1]);
    const day = Number(match[2]);
    const year = Number(match[3]);
    if (month < 1 || month > 12 || year < 1900 || year > 2100) {
      return null;
    }

    let hours = 0;
    let minutes = 0;
    if (match[4] !== undefined && match[5] !== undefined && match[6] !== undefined) {
      const hour12 = Number(match[4]);
      minutes = Number(match[5]);
      const period = match[6];
      if (hour12 < 1 || hour12 > 12 || minutes < 0 || minutes > 59) {
        return null;
      }
      hours = period === 'PM' ? (hour12 % 12) + 12 : hour12 % 12;
    }

    const date = new Date(year, month - 1, day, hours, minutes, 0, 0);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return null;
    }

    return this.formatDateTimeForInput(date);
  }

  private updateEndDateInvalidState(): void {
    const start = this.parseLocalDateTime(this.startDate);
    const end = this.parseLocalDateTime(this.endDate);
    const now = new Date();

    if (!start || !end) {
      this.endDateInvalid = false;
      this.dateRangeValidationMessage = '';
      return;
    }

    if (end.getTime() < start.getTime()) {
      this.endDateInvalid = true;
      this.dateRangeValidationMessage = 'End date/time must be greater than start date/time.';
      return;
    }

    this.endDateInvalid = end.getTime() > now.getTime();
    this.dateRangeValidationMessage = '';
  }

  private validateDateRange(): boolean {
    if (!this.startDate || !this.endDate) {
      this.dateRangeValidationMessage = '';
      return false;
    }

    const start = this.parseLocalDateTime(this.startDate);
    const end = this.parseLocalDateTime(this.endDate);
    if (!start || !end) {
      this.endDateInvalid = true;
      this.dateRangeValidationMessage = '';
      return false;
    }

    if (end.getTime() < start.getTime()) {
      this.endDateInvalid = true;
      this.dateRangeValidationMessage = 'End date/time must be greater than start date/time.';
      return false;
    }

    const now = new Date();
    if (end.getTime() > now.getTime()) {
      this.endDateInvalid = true;
      this.dateRangeValidationMessage = '';
      return false;
    }

    this.endDateInvalid = false;
    this.dateRangeValidationMessage = '';
    return true;
  }

  async downloadSplitDocument(file: SourceFileItem, event: Event): Promise<void> {
    event.stopPropagation();
    if (file.startPageNumber === null || file.endPageNumber === null) {
      return;
    }

    if (!this.sourcePdfDoc) {
      await this.loadSourcePdf();
      if (!this.sourcePdfDoc) {
        this.notificationService.showError('PDF not loaded. Please try again.');
        return;
      }
    }

    this.downloadingFileIds.add(file.id);
    this.cdr.markForCheck();

    try {
      const data: Uint8Array = await this.sourcePdfDoc.getData();
      const srcPdf = await PDFDocument.load(data);
      const newPdf = await PDFDocument.create();

      const startIdx = file.startPageNumber - 1;
      const endIdx = file.endPageNumber - 1;
      const pageIndices = Array.from(
        { length: endIdx - startIdx + 1 },
        (_, i) => startIdx + i
      );

      const copiedPages = await newPdf.copyPages(srcPdf, pageIndices);
      for (const page of copiedPages) {
        newPdf.addPage(page);
      }

      const pdfBytes = await newPdf.save();
      const blob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      const baseName = file.fileName.replace(/\.pdf$/i, '');
      link.download = `${baseName}_pages_${file.startPageNumber}-${file.endPageNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      this.notificationService.showError('Failed to download split document');
    } finally {
      this.downloadingFileIds.delete(file.id);
      this.cdr.markForCheck();
    }
  }
}
